import React from 'react';
import { useLocation } from 'react-router-dom';

import Radio from 'antd/es/radio';
import Select from 'antd/es/select';
import { PopoverProps } from 'antd/es/popover';

import moment from 'moment';

import { Rangepicker } from '@common/react/components/Forms/Datepicker/Datepicker';
import AutocompleteFilter from '@common/react/components/Forms/AutocompleteFilter/AutocompleteFilter';
import SelectFilter from '@common/react/components/Forms/SelectFilter/SelectFilter';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import { getSearchParamsFromUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import {
	BaseFilters,
	SortingDirection,
	useItemsProviderContext,
	HandleChangeEventElement,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import FiltersComponent, { FilterNode, getClean, getFilters } from '@common/react/components/UI/FiltersComponent/FiltersPropsHandler';
import { isUndefined } from '@common/react/utils/guards';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import { Lang } from '@common/typescript/objects/Lang';
import LeaveConfirmationModal from '@common/react/components/Modal/LeaveConfirmationModal';
import { ZipCode } from '@common/typescript/objects/ZipCode';
import { DatePickerReact } from '@common/react/components/UI/DatePickerReact/DatePickerReact';
import MultiSelectAuto from '@common/react/components/UI/MultiSelectAuto/MultiSelectAuto';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { getUserNameOrUnnamedWithEmail } from '@common/react/utils/utils';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { useMobileView } from '@common/react/hooks/useMobileView';
import { TimeMinMax } from '@common/react/utils/timeUtils';
import { LanguageNames } from '@common/react/objects/Language';
import { Dictionary } from '@common/react/objects/Core';

import TimeIntervalRadio from '@commonTuna/react/components/UI/TimeIntervalRadio/TimeIntervalRadio';
import { Payer } from '@commonTuna/react/objects/Payer';
import { GlobalProcedure } from '@commonTuna/react/objects/GlobalProcedure';
import { Specialty } from '@commonTuna/react/objects/Specialty';
import EnumMultipleFilter from '@commonTuna/react/components/UI/EnumMultipleFilter/EnumMultipleFilter';
import { DashboardTimeInterval } from '@commonTuna/react/components/Utils';
import {
	Gender,
	GenderNames,
	TypeOfCoverage,
	TypeOfCoverageNames,
} from '@commonTuna/react/objects/Enums';
import { Feature } from '@commonTuna/react/objects/CompanyFeature';
import { Device, DeviceIcon, DeviceTitle } from '@commonTuna/react/objects/Device';
import EthnicitySelect from '@commonTuna/react/components/Forms/EthnicitySelect/EthnicitySelect';
import { InquirySystem } from '@commonTuna/react/objects/InquirySystem';
import { PageType } from '@commonTuna/react/objects/Page';
import { TemplateType } from '@commonTuna/react/objects/BaseTemplate';
import { RelationShipToInsuredEnum } from '@commonTuna/react/objects/BaseInsurance';
import ColorLine from '@commonTuna/react/components/UI/ColorLine/ColorLine';

import { User, UserRole } from '@app/objects/User';
import {
	activeFilterGroup,
	callOnlyFilterGroup,
	InventoryFiltersNames,
	Patient,
	PatientDocumentValidityNames,
	PatientInfoStatusNames,
	PatientParentType,
	patientParentTypeGroup,
	PatientInfoStatusColor,
	PatientDocumentValidityColors,
	InventoryFilters,
	PatientDocumentValidity,
	PatientInfoStatus,
	ActiveFilterEnum, PatientType,
} from '@app/objects/Patient';
import { Disease } from '@app/objects/Disease';
import { Room } from '@app/objects/Room';
import {
	InsuranceTargetStatus,
	InsuranceTargetStatusNames,
	OrderTargetType,
	OrderType,
	PaymentType,
} from '@app/objects/Order';
import { AppointmentInsuranceStatusEnumNames, RelationShipToInsuredEnumNames } from '@app/objects/AppointmentInsurance';
import {
	OrderStatusFilterEnum, orderStatusFilterGroup, ReviewStatus, ReviewStatusNames,
} from '@app/objects/Appointment';
import StatusFilter from '@app/components/UI/AppointmentStatus/StatusFilter';
import SystemTypeGroupFilter from '@app/components/Forms/SystemTypeGroupFilter/SystemTypeGroupFilter';
import {
	AppointmentReportFilters,
	hasAppointmentGroup,
	ReceivedValue,
	ReportOrderType,
	ReportSourceType,
	ReportSourceTypeAppointmentTime,
	ReportSourceTypeInquiry,
	TargetType,
	TimePriority,
	GroupItem,
	ReportServiceType,
} from '@app/components/Pages/Admin/Reports/Base/BaseReport';
import { ObjectHistoryType, objectHistoryTypeFilterGroup } from '@app/components/Pages/Admin/Appointments/AppointmentHistory';
import MultiplePaymentTypeFilter from '@app/components/Forms/MultiplePaymentTypeFilter/MultiplePaymentTypeFilter';
import DoctorAutocompleteFilter from '@app/components/UI/DoctorAutocompleteFilter';
import { Profession } from '@app/objects/Profession';
import PartitionFilter from '@app/components/Forms/PartitionFilter/PartitionFilter';
import ReportSourceTypeSelect from '@app/components/UI/ReportSourceTypeSelect/ReportSourceTypeSelect';
import { RoundToggleButton } from '@app/components/UI/NumberWithComma/NumberWithCommaProvider';
import SPSDFilters from '@app/components/Forms/SPSDFilters/SPSDFilters';
import AppointmentTypesFilter from '@app/components/UI/AppointmentType/AppointmentTypesFilter';
import { InquiryStatus, InquiryStatusArray, InquiryStatusNames } from '@app/objects/Inquiry';
import DisabledFilter from '@app/components/Forms/DisabledFilter/DisabledFilter';
import WorkingTimeFilter from '@app/components/Forms/WorkingTimeFilter/WorkingTimeFilter';
import TemplateSignedFilter from '@app/components/Forms/TemplateSignedFilter/TemplateSignedFilter';
import TemplateAutocompleteFilter from '@app/components/UI/TemplateAutocompleteFilter';
import ProcedureGroupsFilter from '@app/components/Forms/ProcedureGroupsFilter/ProcedureGroupsFilter';
import { RangeInputs } from '@app/components/UI/RangeInputs/RangeInputs';
import { SectionTitle } from '@app/components/UI/SectionTitle/SectionTitle';
import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import AccrualSwitcher from '@app/components/UI/AccrualSwitcher/AccrualSwitcher';
import { Page } from '@app/objects/Page';
import PhoneSimpleSearch from '@app/components/UI/PhoneSimpleSearch/PhoneSimpleSearch';
import CustomRangePicker from '@app/components/UI/CustomRangePicker/CustomRangePicker';
import UserFiltersComponent from '@app/components/UI/UserFiltersComponent/UserFiltersComponent';
import { UserFilters } from '@app/objects/UserFilters';
import LocationSelect from '@app/components/UI/LocationSelect/LocationSelect';
import { AllergyTypeName } from '@app/objects/Allergy';
import VitalsFilter from '@app/components/UI/VitalsFilter/VitalsFilter';
import { getPopupContainer } from '@app/components/Utils';
import { RelationshipToLicenseHolderEnumNames } from '@app/objects/DrivingLicense';
import AppointmentFiltersTabs from '@app/components/Pages/Admin/Appointments/AppointmentFiltersTabs';
import { SystemTypes } from '@app/objects/Dashboard';

import tunaLogo from '@images/tuna.png';

export const FilterTabAppointmentKey = 'appointment';
export const FilterTabOrderKey = 'order';
export const FilterTabPatientKey = 'patient';
export const FilterTabInquiryKey = 'inquiry';
export const FilterTabConsentFormKey = 'consentForm';

const FilterTabKeys = [
	FilterTabAppointmentKey,
	FilterTabOrderKey,
	FilterTabPatientKey,
	FilterTabInquiryKey,
	FilterTabConsentFormKey,
];

export interface AdditionalTabsFilters {
	[key: string]: (filters: any, handleChange: any) => void;
}

export interface TabFilter {
	key: string;
	label: any;
	className: string;
	visible: boolean;
	children: any;
}

export const orderParamsMap = {
	diseaseId: 'orderDiseaseId',
	payerId: 'orderPayerId',
	doctorIds: 'orderDoctorIds',
	appointmentTypeIds: 'orderAppointmentTypeIds',
	procedureIds: 'orderProcedureIds',
	procedureGroupIds: 'orderProcedureGroupIds',
	userIds: 'orderUserIds',
	specialtyIds: 'orderSpecialtyIds',
	professionIds: 'orderProfessionIds',
	globalProcedureIds: 'orderGlobalProcedureIds',
	disabled: 'orderDisabled',
};

export const filtersAllowNullMap = {
	timeMin: true,
	timeMax: true,
	to: true,
	from: true,
};

export const appointmentFiltersMap = [
	'from',
	'to',
	'mainProcedures',
	'appointmentTypeIds',
	'claimStatusIds',
	'diseaseId',
	'doctorIds',
	'timeMin',
	'hasInsurance',
	'hasAppointmentInsurance',
	'locationId',
	'orderPriorityFiltering',
	'orderStatus',
	'patientId',
	'payerId',
	'procedureGroupIds',
	'procedureIds',
	'productIds',
	'roomId',
	'specialsIds',
	'statusId',
	'statusIds',
	'systemType',
	'patientSystemType',
	'timeInterval',
	'timeMax',
	'userIds',
	'paymentType',
	'paymentTypes',
	'disabled',
	'categoryIds',
	'orderTypes',
	'insuranceTargetStatuses',
	'orderTargetTypes',
	'orderTargetType',
	'inquiryStatus',
	'inquiryLocationId',
	'inquiryDoctorId',
	'withTips',
	'hasDoctorPayer',
	'timePriority',
	'withCredit',
	'withInsurance',
	'withNormal',
	'targetType',
	'byAppointmentTime',
	'byWorkingTimes',
	'templateId',
	'templateSigned',
	'isPatientActive',
	'partition',
	'productUsable',
	'hasProductCategory',
	'hasProcedureGroup',
	'orderHasProcedureGroup',
	'withSubmittedOrderId',
	'hasAppointmentProcedure',
	'gender',
	'giftCard',
	'receivedValue',
	'reportSourceTypes',
	'reviewStatus',
	'text',
	'productActive',
	'hasDoctorProcedure',
	'relatedProcedureIds',
	'relatedProductIds',
	'relatedSpecialsIds',
	'inventoryProcedureIds',
	'inventory',
	'parentId',
	'childId',
	'patientParentType',
	'hasEmail',
	'hasCellPhone',
	'patientHasDrivingLicense',
	'isEmailValid',
	'hasValidDisclosure',
	'hasImportantFlag',
	'companyTemplateExpired',
	'emailConfirmed',
	'phoneNumberConfirmed',
	'patientType',
	'patientHasInsurance',
	'callOnly',
	'birthDate',
	'inquiryGender',
	'inquiryLanguage',
	'inquirySystem',
].concat(Object.keys(orderParamsMap).map((key) => orderParamsMap[key]));

export interface TimeMinMaxPartition extends TimeMinMax {
	partition?: number;
}

type HandleChangeType = (
	params?: Omit<Partial<AppointmentFiltersType>, 'currentTarget'> | HandleChangeEventElement,
	withoutSaveUserFilters?: boolean,
) => void;

export interface AppointmentFiltersType extends BaseFilters, TimeMinMaxPartition {
	locationId?: number;
	from?: number;
	to?: number;
	doctorId?: number;
	doctorIds?: Array<number>;
	procedureId?: number;
	procedureIds?: Array<number>;
	productId?: number;
	productIds?: Array<number>;
	procedureGroupId?: number;
	procedureGroupIds?: Array<number>;
	professionId?: number;
	professionIds?: Array<number>;
	statusId?: number;
	statusIds?: Array<number>;
	appointmentTypeId?: number;
	appointmentTypeIds?: Array<number>;
	claimStatusIds?: Array<number>;
	diseaseId?: number;
	hasInsurance?: boolean;
	hasAppointmentInsurance?: boolean;
	orderPriorityFiltering?: boolean;
	orderStatus?: number;
	patientId?: number;
	patientIds?: Array<number>;
	parentId?: number | null | string;
	childId?: number;
	birthDate?: number;
	payerId?: number;
	templateId?: number;
	categoryIds?: Array<number>;
	roomId?: number;
	roomIds?: Array<number>;
	specialId?: number;
	specialsIds?: Array<number>;
	userId?: number;
	userIds?: Array<number>;
	systemType?: number;
	patientSystemType?: number;
	paymentType?: number;
	disabled?: boolean;
	orderTimePriority?: boolean;
	objectHistoryType?: ObjectHistoryType;
	paymentTypes?: Array<PaymentType>;
	orderTypes?: Array<OrderType>;
	insuranceTargetStatuses?: Array<InsuranceTargetStatus>;
	reportSourceTypes?: Array<ReportSourceType>;
	timePriority?: TimePriority;
	hasProcedureGroup?: boolean;
	reviewStatus?: ReviewStatus;
	mainProcedures?: boolean;
	byWorkingTimes?: boolean;
	orderAppointmentTypeIds?: Array<number>;
	orderFromPortal?: boolean;
	productUsable?: boolean;
	hasProductCategory?: boolean;
	giftCard?: boolean;
	productActive?: boolean;
	orderProcedureGroupIds?: Array<number>;
	orderHasProcedureGroup?: boolean;
	hasDoctorProcedure?: boolean;
	orderDisabled?: boolean;
	withInsurance?: boolean;
	withCredit?: boolean;
	withNormal?: boolean;
	withSubmittedOrderId?: boolean;
	hasDoctorPayer?: boolean;
	withTips?: boolean;
	targetType?: TargetType;
	orderSystemType?: number;
	gender?: Gender;
	ethnicityId?: number;
	patientZip?: string;
	allergies?: Array<number>;
	vitalIds?: Array<number>;
	patientParentType?: PatientParentType;
	hasEmail?: boolean;
	hasCellPhone?: boolean;
	patientHasInsurance?: boolean;
	patientHasDrivingLicense?: boolean;
	isEmailValid?: boolean;
	hasValidDisclosure?: boolean;
	hasImportantFlag?: boolean;
	companyTemplateExpired?: boolean;
	emailConfirmed?: boolean;
	phoneNumberConfirmed?: boolean;
	patientType?: PatientType;
	isPatientActive?: ActiveFilterEnum;
	hasCopyToId?: boolean;
	callOnly?: boolean;
	email?: boolean;
	sMS?: boolean;
	push?: boolean;
	call?: boolean;
	inventory?: InventoryFilters;
	insuranceType?: TypeOfCoverage;
	relationshipToInsured?: RelationShipToInsuredEnum;
	insuranceValidity?: PatientDocumentValidity;
	patientInsuranceStatus?: PatientInfoStatus;
	insuranceId?: string;
	insuranceGroupId?: string;
	driverNumber?: string;
	relationshipToLicenseHolder?: PatientDocumentValidity;
	patientDrivingLicenseStatus?: PatientInfoStatus;
	patientFirstName?: string;
	patientLastName?: string;
	patientEmail?: string;
	patientPhone?: string;
	inquiryStatus?: InquiryStatus;
	drivingLicenseValidity?: PatientDocumentValidity;
	inquiryDoctorId?: number;
	mainPageId?: number;
	dateMin?: number | null;
	dateMax?: number | null;
	inquiryGender?: Gender;
	inquiryLanguage?: Lang;
	inquirySystem?: InquirySystem;
	path?: string;
	device?: Device;
	hasAppointment?: boolean;
	timeMaxSignedTemplate?: number | null;
	timeMinSignedTemplate?: number | null;
	templateSigned?: boolean;
	batchTimePriority?: boolean;
	reportSourceType?: ReportSourceType;
	receivedValue?: ReceivedValue;
	withLocations?: boolean;
	employeeIds?: Array<number> | string;
	paid?: boolean;
	withTimePeriod?: boolean;
	groupItem?: GroupItem;
	part?: number | null;
	page?: number;
	roles?: Array<UserRole> | [];
	reportServiceType?: ReportServiceType;
	fromHistory?: boolean;
	systemTypeColumns?: Array<SystemTypes>;
	orderTargetTypes?: Array<OrderTargetType>;
	patientText?: string;
	banned?: boolean;
	hasRefund?: boolean;
	showOnCalendar?: boolean;
	orderRoles?: Array<UserRole>;
	orderBanned?: boolean;
	inquiryLocationId?: number;
	defaultLocationId?: number;
	isDuplicate?: boolean;
	globalProcedureIds?: Array<number>;
	priceMax?: number;
	priceMin?: number;
	specialtyIds?: Array<number>;
	orderProcedureIds?: Array<number>;
	orderGlobalProcedureIds?: Array<number>;
	orderDoctorIds?: Array<number>;
	orderProfessionIds?: Array<number>;
	orderSpecialtyIds?: Array<number>;
	orderUserIds?: Array<number>;
	relatedProcedureIds?: Array<number>;
	relatedProductIds?: Array<number>;
	relatedSpecialsIds?: Array<number>;
	inventoryProcedureIds?: Array<number>;
	inventoryProductIds?: Array<number>;
	inventorySpecialsIds?: Array<number>;
	changeInnerOrder?: string | number | boolean;
	appointmentLocationId?: number;
	patientFilterGroupIds?: Array<number>;
	patientFilters?: Array<number>;
	isMatched?: boolean;
	isProcessed?: boolean;
}

interface AppointmentsFiltersProps {
	initialValues?: any;
	forHistory?: boolean;
	filterList: Array<AppointmentReportFilters>;
	filterEnabled?: boolean;
	appointmentUtc?: boolean;
	reportSourceTypeExclude?: Array<ReportSourceType>;
	reportSourceTypeDefault?: Array<ReportSourceType>;
	priorityTimes?: Array<TimePriority>;
	utcOffset?: boolean;
	rangePickerFormat?: string;
	customRangePicker?: any;
	customTimeInterval?: any;
	saveBatchTimePriority?: boolean;
	additionalFilters?: (filters: AppointmentFiltersType, handleChange: HandleChangeType) => any;
	additionalTabsFilters?: AdditionalTabsFilters;
	additionalTab?: (filters: AppointmentFiltersType, handleChange: HandleChangeType) => TabFilter;
	className?: string;
	reportSourceTypePlaceAsSearch?: boolean;
	filterComponentTitle?: string;
	popoverClassName?: string;
	overrideReportSourceTypeNames?: object;
	additionalOrderTargetTypes?: Array<OrderTargetType>;
	defaultStatusesValueIsEmpty?: boolean;
	withFiltersComponent?: boolean;
	clearFilters?: BaseParams;
	afterInnerAdditionalFiltersClear?: () => void;
	getFiltersContainer?: (node: HTMLElement) => HTMLElement;
	customTimePriorityButtons?: (filters: AppointmentFiltersType, handleChange: HandleChangeType) => any;
	additionalTabsTitles?: Dictionary<string>;
	showFullInquiryFilters?: boolean;
	withAllLocation?: boolean;
	customDoctorIdsFilter?: React.ReactNode;
	excludeIntervals?: Array<DashboardTimeInterval>;
	moreFilters?: React.ReactNode;
	filtersPlacement?: PopoverProps['placement'] | null;
	customFiltersHandler?: { [K in string]: (param: any) => boolean};
	beforeTabsFilters?: (filters: AppointmentFiltersType, handleChange: HandleChangeType) => React.ReactNode;
	keys?: any;
	setKeys?: React.Dispatch<any>;
	locationLeft?: boolean;
}

const filtersForClearForHistory = [
	'locationId',
];

export const InitialFilters = {
	column: [{ caption: 'scheduledTime', direction: SortingDirection.Descending }],
	objectHistoryType: ObjectHistoryType.All,
	orderTypes: [],
	insuranceTargetStatuses: [],
	procedureIds: [],
};

const { Option } = Select;

const defaultFilters = {
	statusIds: [],
};

const patientFiltersData = {
	patientName: '',
	parentName: '',
	childName: '',
	inventorySpecials: [],
	inventoryProcedures: [],
	inventoryProducts: [],
};

const orderFilterData = {
	orderDiseaseName: '',
	orderPayerName: '',
	orderProcedures: [],
	relatedProcedures: [],
	relatedProducts: [],
	relatedSpecials: [],
	products: [],
	specials: undefined,
	orderGlobalProcedures: [],
	orderSpecialties: [],
	orderProfessions: [],
};

const consentFormFilterData = {
	templateName: '',
};

const appointmentFiltersData = {
	diseaseName: '',
	procedures: [],
	orderAppointmentTypes: [],
	users: [],
	globalProcedures: [],
	specialties: [],
	professions: [],
};

const inquiryFilterData = {
	mainPageName: '',
	pageName: '',
	inquiryDoctor: undefined,
};

const resetValues = {
	patient: patientFiltersData,
	order: orderFilterData,
	appointment: appointmentFiltersData,
	consentForm: consentFormFilterData,
	inquiry: inquiryFilterData,
};

const withoutEntity = -2;

const defaultFiltersHandler: { [key in string]: (value: any) => boolean } = {
	objectHistoryType: (value) => !!value,
	byWorkingTimes: (value) => !!value,
	orderPriorityFiltering: (value) => !!value,
	diseaseId: (value) => !!value && value > 0,
	payerId: (value) => !!value && value > 0,
	inquiryLocationId: (value) => !!value && value > 0,
	locationId: (value) => !!value && value > 0,
	orderDiseaseId: (value) => !!value && value > 0,
	orderPayerId: (value) => !!value && value > 0,
	patientId: (value) => !!value && value > 0,
	parentId: (value) => !!value && value > 0,
	childId: (value) => !!value && value > 0,
	birthDate: (value) => !!value && value > 0,
	mainPageId: (value) => !!value && value > 0,
	pageId: (value) => !!value && value > 0,
	inquiryDoctorId: (value) => !!value && (value > 0 || +value === withoutEntity),
	ethnicityId: (value) => !!value && value > 0,
	defaultDoctorId: (value) => !!value && value > 0,
	defaultLocationId: (value) => !!value && value > 0,
	inventory: (value) => !!value && value > 0,
	templateId: (value) => !!value && value > 0,
	callOnly: (value) => !!value && value > 0,
	isPatientActive: (value) => !!value && value > 0,
	mainProcedures: (value) => !!value,
	patientParentType: (value) => !!value && value > 0,
	inquiryStatus: (value) => typeof value === 'number' && value !== InquiryStatus.All,
	withInsurance: (value) => !!value,
	withCredit: (value) => !!value,
	withNormal: (value) => !!value,
	targetType: (value) => !!value,
	insurancePayerId: (value) => !!value && value > 0,
	timeInterval: (value) => !!value && value > 0,
};

const transformChildrenList = (
	node,
	filters: AppointmentFiltersType,
	filtersHandler: { [key in string]: (value: any) => boolean },
	transformInnerArray?: boolean,
) => {
	if (!node?.props?.children) return;

	if (Array.isArray(node)) {
		return React.Children.map(
			node,
			(child) => <FilterNode
				{...(typeof child === 'object' ? child?.props : {})}
				filtersHandler={filtersHandler}
				filters={filters}
			>
				{child}
			</FilterNode>,
		);
	}

	return React.cloneElement(node, {
		...node?.props,
		children: React.Children.map(
			node?.props?.children,
			(child) => child && (
				transformInnerArray && Array.isArray(child?.props?.children)
					? transformChildrenList(child, filters, filtersHandler, transformInnerArray)
					: (
						<FilterNode
							{...(typeof child === 'object' ? child?.props : {})}
							filtersHandler={filtersHandler}
							filters={filters}
						>
							{child}
						</FilterNode>
					)),
		),
	});
};

const getParamsFromNode = (node) => {
	const clearFilters = {};
	React.Children.map(node?.props?.children, (filter) => {
		const props = filter?.props;
		if (props && (props['data-param'] || props.param)) {
			if (Array.isArray(props['data-param'] || props.param)) {
				(props['data-param'] || props.param).forEach((param) => clearFilters[param] = undefined);
			} else {
				clearFilters[props['data-param'] || props.param] = undefined;
			}
		}
	});
	return clearFilters;
};

const getJsonMessage = (params) => {
	return JSON.stringify(params || {}, (key, value) => {
		if (!value && Array.isArray(params?.[key])) {
			return [];
		}

		return isUndefined(value) ? null : value;
	});
};

const solveChildrenList = (element, array) => {
	if (Array.isArray(element)) {
		React.Children.forEach(element, (el) => {
			array.push(el);
			!el?.props?.filters && solveChildrenList(el?.props?.children, array);
		});
	} else {
		element && array.push(element);
	}
};

const resize = () => (typeof window === 'undefined' ? false : window.innerWidth < 768 || window.innerHeight < 460);

const AppointmentsFilters: React.FC<AppointmentsFiltersProps> = ({
	initialValues: initValues,
	forHistory,
	filterList,
	filterEnabled,
	appointmentUtc,
	reportSourceTypeExclude,
	reportSourceTypeDefault = [ReportSourceType.Appointment],
	priorityTimes = [TimePriority.Appointment, TimePriority.Order, TimePriority.Batch, TimePriority.Inquiry],
	utcOffset = true,
	rangePickerFormat = 'MM/DD/YYYY',
	customRangePicker,
	customTimeInterval,
	saveBatchTimePriority,
	additionalFilters,
	additionalTabsFilters,
	additionalTab,
	className = '',
	reportSourceTypePlaceAsSearch = false,
	filterComponentTitle = '',
	popoverClassName,
	overrideReportSourceTypeNames,
	additionalOrderTargetTypes,
	defaultStatusesValueIsEmpty,
	withFiltersComponent = true,
	clearFilters,
	afterInnerAdditionalFiltersClear,
	getFiltersContainer,
	customTimePriorityButtons,
	additionalTabsTitles,
	showFullInquiryFilters = true,
	withAllLocation = true,
	customDoctorIdsFilter,
	excludeIntervals,
	moreFilters,
	filtersPlacement,
	customFiltersHandler = {},
	beforeTabsFilters,
	keys: propsKeys,
	setKeys: propsSetKeys,
	locationLeft,
}) => {
	const initialValues = {
		...initValues,
		statuses: undefined,
		locations: undefined,
		appointmentTypes: undefined,
		defaultStatuses: undefined,
	};
	const { request, getUserUtcOffset } = useApplicationContext();
	const filtersHandler = {
		...defaultFiltersHandler,
		...customFiltersHandler,
	};
	const userUtcOffset = utcOffset ? getUserUtcOffset() : undefined;
	const location = useLocation();
	const { openConfirm, openErrorMessage } = useModal();
	const getSelectFilterContainer = location.pathname.includes('dashboard')
		? ((node: HTMLElement) => (node.closest('.ant-tabs-tabpane') || document.body) as HTMLElement)
		: getPopupContainer;
	const isMobile = useMobileView(768, (setIsMobile) => setIsMobile(resize()), resize);
	const rangePickerContainer = isMobile ? (element: HTMLElement) => ((element.closest('.dashboard-page__widgets') || document.body) as HTMLElement)
		: getSelectFilterContainer;

	const { state: { filters }, actions: { handleChange: onChange } } = useItemsProviderContext<any, AppointmentFiltersType>();

	const [values, setValues] = React.useState<BaseParams | null>(initialValues || null);
	const [_keys, _setKeys] = React.useState<any>({ key: 'initial' });
	const [keys, setKeys] = [propsKeys || _keys, propsSetKeys || _setKeys];
	const [open, setOpen] = React.useState(false);
	const [filtersChanged, setFiltersChanged] = React.useState(false);
	const [selectedUserFilters, _setSelectedUserFilters] = React.useState<UserFilters | undefined>();

	const setSelectedUserFilters = (value: UserFilters | undefined) => {
		_setSelectedUserFilters(value);
		setFiltersChanged(false);
	};

	const saveCurrentFilters = (params: AppointmentFiltersType) => {
		setFiltersChanged(false);
		return request('userFilters', {
			...selectedUserFilters,
			actorType: 'appointmentFilters',
			messageGroup: initialValues?.messageGroup,
			jsonMessage: JSON.stringify(params || {}, (key, value) => {
				if (!value && Array.isArray(params?.[key])) {
					return [];
				}

				return isUndefined(value) ? null : value;
			}),
		})
			.catch((err) => openErrorMessage?.(err));
	};

	const handleChange: HandleChangeType = (
		params?: AppointmentFiltersType | HandleChangeEventElement,
		withoutSaveUserFilters?: boolean,
	) => {
		onChange(params);
		setFiltersChanged(true);
		if (!withoutSaveUserFilters && selectedUserFilters && getJsonMessage(params) !== getJsonMessage(selectedUserFilters.jsonMessage)) {
			setOpen(false);
			openConfirm({
				title: `Filters have been changed. Save the current filter combination into a combination named "${selectedUserFilters.name}"?`,
				onOk: () => {
					if (params && 'currentTarget' in params) {
						const { name, value } = params.currentTarget;
						saveCurrentFilters({ ...filters, [name]: value });
						return;
					}
					saveCurrentFilters({ ...filters, ...params });
				},
			});
		}
	};
	const isDefaultValueReportSourceTypes = `${reportSourceTypeDefault.slice().sort()}` === `${filters.reportSourceTypes?.map((rst) => rst).sort()}`;
	const reportSourceTypesFilter = (<ReportSourceTypeSelect
		className={reportSourceTypePlaceAsSearch ? ' ' : undefined}
		containerClassName={`report-source-types-filter ${
			isDefaultValueReportSourceTypes && filters.reportSourceTypes?.length === 1 ? 'report-source-types-filter__default' : ''
		} ${reportSourceTypePlaceAsSearch ? 'pull-left mr10' : 'pull-right'}`}
		multiple
		style={reportSourceTypePlaceAsSearch ? { width: '100%', minWidth: 250 } : undefined}
		additionalParams={filters}
		maxTagTextLength={reportSourceTypePlaceAsSearch ? 25 : undefined}
		value={filters.reportSourceTypes?.map((rst) => rst)}
		overrideNames={overrideReportSourceTypeNames}
		selectProps={{
			allowClear: !isDefaultValueReportSourceTypes,
		}}
		onChange={(value: Array<ReportSourceType> | ReportSourceType) => {
			let values = value as Array<ReportSourceType>;

			if (!prevReportSourceTypes.some((v) => v === ReportSourceType.Patient)
				&& values.some((v) => v === ReportSourceType.Patient)) {
				values = [ReportSourceType.Patient];
			} else {
				values = values.filter((v) => v !== ReportSourceType.Patient);
			}

			if (!values.length) {
				handleChange({
					timePriority: saveBatchTimePriority ? filters.timePriority : TimePriority.Appointment,
					reportSourceTypes: reportSourceTypeDefault,
				});
			} else if (values.some((v) => ReportSourceTypeAppointmentTime.includes(v))) {
				handleChange({
					timePriority: saveBatchTimePriority ? filters.timePriority : TimePriority.Appointment,
					reportSourceTypes: values,
				});
			} else {
				handleChange({
					reportSourceTypes: values,
				});
			}

			setPrevReportSourceTypes(values);
		}}
		excludeTypes={reportSourceTypeExclude}
	/>);

	const additionalParams: any = {};
	const innerKeyTabFilters: any = {};

	FilterTabKeys.map((key) => {
		const innerKeyTabFilter = additionalTabsFilters && additionalTabsFilters[key]
			&& additionalTabsFilters[key](filters, handleChange);

		innerKeyTabFilters[key] = innerKeyTabFilter;

		additionalParams[key] = innerKeyTabFilter
			? Object.keys(getParamsFromNode(innerKeyTabFilter))
			: [];
	});

	const [prevReportSourceTypes, setPrevReportSourceTypes] = React.useState<Array<ReportSourceType>>(filters.reportSourceTypes
		|| reportSourceTypeDefault);
	React.useEffect(() => {
		if (!initialValues) {
			const params = getSearchParamsFromUrl(location) as AppointmentFiltersType;
			request<any>('appointmentListFilter', params)
				.then((res) => {
					setValues(res);
					setKeys({
						patientId: Math.random(),
						parentId: Math.random(),
						childId: Math.random(),
						birthDate: Math.random(),
						doctorId: Math.random(),
						diseaseId: Math.random(),
						payerId: Math.random(),
						templateId: Math.random(),
						procedureIds: Math.random(),
						appointmentTypeIds: Math.random(),
						userIds: Math.random(),
						productIds: Math.random(),
						paymentType: Math.random(),
						roomIds: Math.random(),
					});
				})
				.catch((err) => console.log(err));
		}
	}, []);

	const shouldShowFilter = (type: AppointmentReportFilters | Array<AppointmentReportFilters>) => {
		if (Array.isArray(type)) {
			return !!type.filter((type) => (filterEnabled ? filterList.includes(type) : !filterList.includes(type))).length;
		}
		return filterEnabled ? filterList.includes(type) : !filterList.includes(type);
	};

	const orderDisabledWidth = React.useMemo(() => {
		return location.pathname.includes('order-details') ? 26
			: shouldShowFilter(AppointmentReportFilters.OrderPriorityFiltering) ? 18 : 33.3;
	}, [location]);

	const { checkFeatureAccess } = useCompanyFeatureProviderContext();

	const filtersTabs: Array<TabFilter> = [
		{
			key: FilterTabAppointmentKey,
			label: <>
				<i className="fa fa-calendar-check-o" />
				<span className="tab-text">
					{' '}
					Appointment
				</span>
			</>,
			className: 'filters-container',
			visible: true,
			children: <>
				{shouldShowFilter(
					[
						AppointmentReportFilters.InnerLocationId,
						AppointmentReportFilters.InnerStatusIds,
						AppointmentReportFilters.RoomIds,
					],
				)
					&& <SectionTitle title="General" />}
				{shouldShowFilter(AppointmentReportFilters.InnerLocationId)
					? <div data-param="locationId" className="filter-element">
						<LocationSelect
							getPopupContainer={getSelectFilterContainer}
							selectProps={{
								className: 'pull-right',
								value: filters.locationId,
							}}
							withoutAllLocation={!withAllLocation}
							onChange={(evt) => {
								handleChange({
									locationId: evt.currentTarget.value,
									roomIds: undefined,
								});
								setKeys((prev: any) => ({ ...prev, roomIds: Math.random() }));
							}}
						/>
					</div> : <></>}
				{shouldShowFilter(AppointmentReportFilters.InnerStatusIds)
					? <div data-param="statusIds" className="filter-element multi-select-filter">
						<StatusFilter
							value={filters.statusIds ? (filters.statusIds || []).map((s) => +s) : []}
							onChange={(value) => handleChange({ statusIds: value })}
							defaultValueIsEmpty={defaultStatusesValueIsEmpty}
							getPopupContainer={getSelectFilterContainer}
						/>
					</div> : <></>}
				{shouldShowFilter(AppointmentReportFilters.RoomIds) && <div data-param="roomIds" className="multi-select-filter filter-element">
					<MultiSelectAuto
						key={keys.roomIds}
						type="roomSelectViewList"
						renderTitle={(item: Room) => item.name}
						placeholder="Rooms..."
						loadOnFocus
						onChange={(value) => handleChange({ roomIds: value as any })}
						items={values?.rooms || [] as any}
						antdProps={{
							defaultValue: values?.rooms as any || [],
							allowClear: true,
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						params={{
							locationId: filters.locationId ? +filters.locationId : 0,
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.AppointmentTypeIds)
				&& <div data-param="appointmentTypeIds" className="multi-select-filter filter-element">
					<AppointmentTypesFilter
						key={keys.appointmentTypeIds || 'appointmentTypeIds'}
						value={filters.appointmentTypeIds}
						onChange={handleChange}
						withAdditionalOptions
						antdProps={{ getPopupContainer: getSelectFilterContainer }}
					/>
				</div>}
				{shouldShowFilter([
					AppointmentReportFilters.ProcedureIds,
					AppointmentReportFilters.ProcedureGroupIds,
					AppointmentReportFilters.GlobalProcedureIds,
					AppointmentReportFilters.HasProcedureGroup,
				]) && <SectionTitle title="Services" />}
				{shouldShowFilter(AppointmentReportFilters.ProcedureIds)
					&& <span data-param="procedureIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.procedureIds || 'procedureIds'}
							type="procedureList"
							onChange={(value) => handleChange({ procedureIds: value as any })}
							placeholder="Services..."
							loadOnFocus
							antdProps={{
								defaultValue: values?.procedures || [],
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.procedures || []}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.ProcedureGroupIds)
					&& <span data-param="procedureGroupIds" className="multi-select-filter filter-element">
						<ProcedureGroupsFilter
							key="procedureGroupIds"
							value={filters.procedureGroupIds}
							onChange={handleChange}
							withAdditionalOptions
							procedureGroups={values?.procedureGroups}
							antdProps={{ getPopupContainer: getSelectFilterContainer }}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.GlobalProcedureIds)
					&& <div className="filter-element multi-select-filter" data-param="globalProcedureIds">
						<MultiSelectAuto
							key={keys.globalProceduresList || keys.key}
							type="globalProcedureViewList"
							renderTitle={(item: GlobalProcedure) => item.name}
							placeholder="Global Services..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ globalProcedureIds: value.map((q) => +q) });
							}}
							items={values?.globalProcedures}
							antdProps={{
								defaultValue: values?.globalProcedures || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.HasProcedureGroup)
					&& <span
						data-param="hasProcedureGroup"
						className="responsive-radio responsive-radio__short"
						style={{ maxWidth: 'calc(33.3% - 20px)' }}
					>
						<Radio.Group
							key={keys.hasProcedureGroup}
							className="pull-right"
							name="hasProcedureGroup"
							value={filters.hasProcedureGroup ?? null}
							onChange={(e) => {
								handleChange({ hasProcedureGroup: e.target.value });
							}}
						>
							<Radio.Button value title="Has Procedure Group">
								<i className="fa fa-object-group" aria-hidden="true" />
							</Radio.Button>
							<Radio.Button value={false} title="No Procedure Group">
								<i className="fa fa-object-group" aria-hidden="true" />
								<i className="fa fa-times times-icon" />
							</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter([
					AppointmentReportFilters.DoctorIds,
					AppointmentReportFilters.ProfessionIds,
					AppointmentReportFilters.SpecialtyIds,
					AppointmentReportFilters.Disabled,
				]) && <SectionTitle title="Providers" />}
				{shouldShowFilter(AppointmentReportFilters.DoctorIds) && <div data-param="doctorIds" className="multi-select-filter filter-element">
					{customDoctorIdsFilter || <DoctorAutocompleteFilter
						multiple
						key={keys.doctorId}
						onChange={(value: any) => handleChange({ doctorIds: value })}
						items={values?.doctors || []}
						antdProps={{
							defaultValue: values?.doctors || [],
							allowClear: true,
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
					/>}
				</div>}
				{shouldShowFilter(AppointmentReportFilters.ProfessionIds)
					&& <div className="filter-element multi-select-filter" data-param="professionIds">
						<MultiSelectAuto
							key={keys.professionIds}
							type="professionList"
							renderTitle={(item: Profession) => item.nameEn}
							placeholder="Providers Professions..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ professionIds: value.map((q) => +q) });
							}}
							items={values?.professions}
							antdProps={{
								defaultValue: values?.professions || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.SpecialtyIds)
					&& <div className="filter-element multi-select-filter" data-param="specialtyIds">
						<MultiSelectAuto
							key={keys.specialtyIds || keys.key}
							type="specialtyViewList"
							renderTitle={(item: Specialty) => item.name}
							placeholder="Specialties..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ specialtyIds: value.map((q) => +q) });
							}}
							items={values?.specialties}
							antdProps={{
								defaultValue: values?.specialties || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.Disabled)
					&& <div
						data-param="disabled"
						className="responsive-radio"
						style={{ flex: '1 1 calc(18% - 20px)', maxWidth: 'calc(33.33% - 20px)' }}
					>
						<DisabledFilter
							className="pull-right custom-radio"
							value={filters.disabled}
							onChange={(value) => {
								handleChange({ disabled: value });
							}}
							withIcons
							allAsText
						/>
					</div>}

				{shouldShowFilter([
					AppointmentReportFilters.PayerId,
					AppointmentReportFilters.ClaimStatusIds,
				]) && <SectionTitle title="Insurance" />}

				{shouldShowFilter(AppointmentReportFilters.PayerId) && <AutocompleteFilter<Payer>
					key={keys.payerId || 'payerId'}
					type="payerViewList"
					renderTitle={(item: Payer) => {
						return `${item.code} ${item.name}`;
					}}
					handleChangeAndRefresh={handleChange}
					param="payerId"
					value={values?.payerName ?? ''}
					placeholder="Payer..."
					loadOnFocus
				/>}
				{shouldShowFilter(AppointmentReportFilters.ClaimStatusIds)
					&& <span data-param="claimStatusIds" className="multi-select-filter filter-element">
						<EnumMultipleFilter
							getPopupContainer={getSelectFilterContainer}
							className="pull-right ml10 mb10"
							placeholder="Claim statuses..."
							value={(filters.claimStatusIds || []).map((key) => +key)}
							onChange={(value) => {
								handleChange({ claimStatusIds: value });
							}}
							selectOptions={AppointmentInsuranceStatusEnumNames}
							maxTagCount={1}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.HasInsurance)
				&& <div data-param="hasInsurance" className="filter-ratio-element filter-radio-element__small">
					<Radio.Group
						className="pull-right ml10 mb10 custom-radio"
						name="hasInsurance"
						value={filters.hasInsurance !== undefined ? filters.hasInsurance : null}
						onChange={(e) => handleChange({ hasInsurance: e.target.value })}
					>
						<Radio.Button value title="Insurance">
							<div className="custom-radio-button">
								<i className="tab-icon fa fa-shield" aria-hidden="true" />
							</div>
						</Radio.Button>
						<Radio.Button value={false} title="Without Insurance">
							<div className="custom-radio-button">
								<i className="tab-icon fa fa-shield" aria-hidden="true" />
								<i className="fa fa-times times-icon" />
							</div>
						</Radio.Button>
						<Radio.Button value={null} className="custom-radio-button">
							All
						</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.HasAppointmentInsurance)
					&& <div data-param="hasAppointmentInsurance" className="filter-ratio-element">
						<Radio.Group
							className="pull-right ml10 mb10 custom-radio"
							name="hasAppointmentInsurance"
							value={filters.hasAppointmentInsurance !== undefined ? filters.hasAppointmentInsurance : null}
							onChange={(e) => handleChange({ hasAppointmentInsurance: e.target.value })}
						>
							<Radio.Button value title="With Claim">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-medkit" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Without Claim">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-medkit" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter([
					AppointmentReportFilters.ReviewStatus,
					AppointmentReportFilters.DiseaseId,
					AppointmentReportFilters.UserIds,
					AppointmentReportFilters.SystemType,
					AppointmentReportFilters.ObjectHistoryType,
					AppointmentReportFilters.ByWorkingTimes,
				]) && <SectionTitle title="System" />}
				{shouldShowFilter(AppointmentReportFilters.ReviewStatus) && <div data-param="reviewStatus" className="multi-select-filter">
					<Select
						getPopupContainer={getSelectFilterContainer}
						placeholder="Review Status..."
						value={filters.reviewStatus}
						onChange={(value) => handleChange({ reviewStatus: value })}
						showSearch={false}
						allowClear
					>
						{Object.keys(ReviewStatusNames).map((key) =>
							<Option value={+key} key={+key}>{ReviewStatusNames[+key]}</Option>)}
					</Select>
				</div>}

				{shouldShowFilter(AppointmentReportFilters.DiseaseId) && <div
					data-param="diseaseId"
					className="multi-select-filter filter-element"
				>
					<AutocompleteFilter<Disease>
						key={keys.diseaseId || 'diseaseId'}
						type="diseaseList"
						renderTitle="name"
						handleChangeAndRefresh={handleChange}
						param="diseaseId"
						value={values?.diseaseName ?? ''}
						placeholder="Medical Questions..."
						loadOnFocus
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.UserIds) && <span data-param="userIds" className="multi-select-filter filter-element">
					<MultiSelectAuto
						key={keys.userIds || 'userIds'}
						type="userAutoCompleteViewList"
						onChange={(value: any) => handleChange({ userIds: value })}
						placeholder="Users..."
						loadOnFocus
						antdProps={{
							defaultValue: (values?.users || []).filter((user) => filters?.userIds?.includes(user?.id)) as any,
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.users}
						renderTitle={(item: User) => `${item.firstName} ${item.lastName}`}
					/>
				</span>}
				{shouldShowFilter(AppointmentReportFilters.SystemType) && <div
					style={{ flex: '0 0 calc(33.33% - 20px)' }}
					data-param="systemType"
					className="responsive-radio"
				>
					<SystemTypeGroupFilter
						className=" "
						systemType={filters.systemType}
						handleChangeAndRefresh={handleChange}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.ObjectHistoryType) && forHistory
					&& <div
						className="responsive-radio"
						data-param="objectHistoryType"
						style={{ flex: '1 1 calc(18% - 20px)', maxWidth: 'calc(33.33% - 20px)' }}
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="objectHistoryType"
							value={filters?.objectHistoryType !== undefined ? +filters.objectHistoryType : ObjectHistoryType.All}
							onChange={(e) => {
								handleChange({ objectHistoryType: e.target.value });
							}}
						>
							{[...objectHistoryTypeFilterGroup].reverse().map((item) => (
								<Radio.Button value={item.value} key={item.value} title={item.caption}>
									{item.value === ObjectHistoryType.All ? <div className="custom-radio-button">All</div>
										: <div title={item.caption} className="custom-radio-button">
											<i
												className={`fa fa-${item.value === ObjectHistoryType.OrderTargets
													? 'usd'
													: 'calendar'}`}
												aria-hidden="true"
											/>
										</div>}
								</Radio.Button>
							))}
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.MainProcedures)
					&& <div
						className="responsive-radio"
						data-param="mainProcedures"
						style={{ flex: '1 1 calc(18% - 20px)' }}
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="mainProcedures"
							value={filters?.mainProcedures ?? false}
							onChange={(e) => {
								handleChange({ mainProcedures: e.target.value });
							}}
						>
							<Radio.Button value>
								<span title="Filtering only by main procedures">Only Main Procedure</span>
							</Radio.Button>
							<Radio.Button value={false}>
								<span title="Filtering by all procedures">All</span>
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.ByWorkingTimes)
					&& <div
						className="responsive-radio"
						style={{ flex: '1 1 calc(12% - 20px)', maxWidth: 'calc(33.33% - 20px)' }}
						data-param="byWorkingTimes"
					>
						<WorkingTimeFilter
							className="pull-right custom-radio"
							value={filters.byWorkingTimes}
							onChange={(value) => {
								handleChange({ byWorkingTimes: value });
							}}
							withIcons
						/>
					</div>}
				{/* --- End Appointment Filters ---*/}
				{/* --- Start Inner Additional Appointment Filters --- */}
				{!isUndefined(additionalTabsFilters?.[FilterTabAppointmentKey]) && innerKeyTabFilters[FilterTabAppointmentKey] && <>
					{(additionalTabsTitles?.[FilterTabAppointmentKey] === undefined || additionalTabsTitles?.[FilterTabAppointmentKey])
						&& <SectionTitle title={additionalTabsTitles?.[FilterTabAppointmentKey] || 'Additional Filters'} />}
					{transformChildrenList(innerKeyTabFilters[FilterTabAppointmentKey], filters, filtersHandler)}
				</>}
				<div data-param={additionalParams[FilterTabAppointmentKey]} style={{ display: 'none' }} />
				{/* --- End Inner Additional Appointment Filters --- */}
			</>,
		},
		{
			key: FilterTabOrderKey,
			label: <>
				<i className="fa fa-clock-o" />
				<span className="tab-text">
					{' '}
					Order
				</span>
			</>,
			className: 'filters-container',
			visible: true,
			children: <>
				{/* --- Start Order Filters ---*/}
				{shouldShowFilter([
					AppointmentReportFilters.SPSDFilters,
					AppointmentReportFilters.OrderStatus,
					AppointmentReportFilters.OrderTypes,
					AppointmentReportFilters.PaymentTypes,
					AppointmentReportFilters.OrderAppointmentTypeIds,
					AppointmentReportFilters.PriceMinMax,
					AppointmentReportFilters.OrderFromPortal,
					AppointmentReportFilters.InsuranceTargetStatuses,
				]) && <SectionTitle title="General" />}
				{shouldShowFilter(AppointmentReportFilters.SPSDFilters)
					&& <div className="btn-group ratio-group responsive-radio" data-param="orderTargetTypes" role="group">
						<SPSDFilters
							filters={filters}
							handleChange={handleChange}
							orderTargetTypes={additionalOrderTargetTypes}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderStatus) && <SelectFilter
					additionalParams={filters}
					param="orderStatus"
					onChange={handleChange}
					className="pull-right multi-select-filter"
					defaultValue={filters.orderStatus ?? OrderStatusFilterEnum.All}
					currentValue={filters.orderStatus ?? OrderStatusFilterEnum.All}
					selectProps={{
						getPopupContainer: getSelectFilterContainer,
					}}
				>
					{orderStatusFilterGroup.map((item) =>
						<Option value={item.type} key={item.type}>
							{item.caption}
						</Option>)}
				</SelectFilter>}
				{shouldShowFilter(AppointmentReportFilters.OrderTypes) && <div className="filter-element multi-select-filter" data-param="orderTypes">
					<Select
						getPopupContainer={getSelectFilterContainer}
						className={className ?? 'pull-right ml10 multiple-select-filter'}
						mode="multiple"
						placeholder="Order Types..."
						value={filters.orderTypes?.filter((q) => q).map((q) => q.toString()) || []}
						onChange={(value: any) => {
							handleChange({ orderTypes: value });
						}}
						maxTagCount={2}
						maxTagTextLength={10}
						showSearch={{ filterOption: false }}
						allowClear
					>
						{Object.keys(ReportOrderType).map((value) =>
							<Option value={value} key={+value}>{ReportOrderType[value]}</Option>)}
					</Select>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.PaymentTypes)
					&& <div data-param="paymentTypes" className="multi-select-filter filter-element">
						<MultiplePaymentTypeFilter
							getPopupContainer={getSelectFilterContainer}
							maxTagCount={1}
							value={filters.paymentTypes}
							onChange={(value: Array<any>) => handleChange({ paymentTypes: value.map((q) => +q) })}
							excludeTypes={checkFeatureAccess(Feature.GiftCard)
								? [PaymentType.Credit, PaymentType.All]
								: [PaymentType.GiftCard, PaymentType.Credit, PaymentType.All]}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderAppointmentTypeIds)
					&& <div data-param="orderAppointmentTypeIds" className="multi-select-filter filter-element">
						<AppointmentTypesFilter
							valueName="orderAppointmentTypeIds"
							key={keys.orderAppointmentTypeIds || 'orderAppointmentTypeIds'}
							value={filters.orderAppointmentTypeIds}
							onChange={handleChange}
							withAdditionalOptions
							getPopupContainer={getSelectFilterContainer}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceTargetStatuses)
					&& <div className="filter-element multi-select-filter" data-param="insuranceTargetStatuses">
						<Select
							getPopupContainer={getSelectFilterContainer}
							className={className ?? 'pull-right ml10 multiple-select-filter'}
							mode="multiple"
							placeholder="Insurance Target Statuses..."
							value={filters.insuranceTargetStatuses?.map((q) => q.toString()) || []}
							onChange={(value: any) => {
								handleChange({ insuranceTargetStatuses: value });
							}}
							maxTagCount={2}
							maxTagTextLength={10}
							showSearch={false}
							allowClear
						>
							{Object.keys(InsuranceTargetStatusNames).map((value) =>
								<Option value={value} key={+value}>{InsuranceTargetStatusNames[value]}</Option>)}
						</Select>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.PriceMinMax)
					&& <div
						className="filter-range-element"
						data-param={['priceMin', 'priceMax']}
						style={{ display: 'flex', marginRight: 10 }}
					>
						<RangeInputs
							placeholder="Price"
							onChange={handleChange}
							valueName="price"
							filters={filters}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderFromPortal)
					&& <span data-param="orderFromPortal" className="responsive-radio" style={{ maxWidth: 'calc(33.33% - 20px)' }}>
						<Radio.Group
							key={keys.orderFromPortal}
							className="pull-right"
							name="orderFromPortal"
							value={filters.orderFromPortal ?? null}
							onChange={(e) => {
								handleChange({ orderFromPortal: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>From Portal</Radio.Button>
							<Radio.Button value={false}>From Tuna</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}

				{shouldShowFilter([
					AppointmentReportFilters.ProductIds,
					AppointmentReportFilters.SpecialsIds,
					AppointmentReportFilters.Category,
					AppointmentReportFilters.ProductUsable,
					AppointmentReportFilters.HasProductCategory,
					AppointmentReportFilters.GiftCard,
					AppointmentReportFilters.ProductActive,
				]) && <SectionTitle title="Items" />}

				{shouldShowFilter(AppointmentReportFilters.ProductIds)
					&& <span data-param="productIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.productIds || 'productIds'}
							type="productList"
							onChange={(value: any) => handleChange({ productIds: value })}
							placeholder="Products..."
							loadOnFocus
							antdProps={{
								defaultValue: (values?.products || []) as any,
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.products}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.SpecialsIds)
					&& <span data-param="specialsIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.specialsIds || 'specialsIds'}
							type="specialsAutocompleteList"
							onChange={(value: any) => handleChange({ specialsIds: value })}
							placeholder="Specials..."
							loadOnFocus
							antdProps={{
								defaultValue: (values?.specials || []) as any,
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.specials}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.Category)
					&& <span data-param="categoryIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.categoryIds || 'categoryIds'}
							type="categoryAutocompleteViewList"
							onChange={(value: any) => handleChange({ categoryIds: value })}
							placeholder="Categories..."
							loadOnFocus
							antdProps={{
								defaultValue: (values?.categories || []) as any,
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.categories}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.ProductUsable)
					&& <span data-param="productUsable" className="responsive-radio long-radio">
						<Radio.Group
							key={keys.productUsable}
							className="pull-right"
							name="productUsable"
							value={filters.productUsable ?? null}
							onChange={(e) => {
								handleChange({ productUsable: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>Combined Product</Radio.Button>
							<Radio.Button value={false}>Not combined Product</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.HasProductCategory)
					&& <span data-param="hasProductCategory" className="responsive-radio long-radio">
						<Radio.Group
							key={keys.productUsable}
							className="pull-right"
							name="hasProductCategory"
							value={filters.hasProductCategory ?? null}
							onChange={(e) => {
								handleChange({ hasProductCategory: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>Has Product Category</Radio.Button>
							<Radio.Button value={false}>No Product Category</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.GiftCard)
					&& <span data-param="giftCard" className="responsive-radio long-radio">
						<Radio.Group
							key={keys.giftCard}
							className="pull-right"
							name="giftCard"
							value={filters.giftCard ?? null}
							onChange={(e) => {
								handleChange({ giftCard: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>Has Gift Card</Radio.Button>
							<Radio.Button value={false}>No Gift Card</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.ProductActive)
					&& <span data-param="productActive" className="responsive-radio long-radio" style={{ maxWidth: 'calc(33.33% - 20px)' }}>
						<Radio.Group
							key={keys.productActive}
							className="pull-right"
							name="productActive"
							value={filters.productActive ?? null}
							onChange={(e) => {
								handleChange({ productActive: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>Product Active</Radio.Button>
							<Radio.Button value={false}>Product Not Active</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter([
					AppointmentReportFilters.OrderGlobalProcedureIds,
					AppointmentReportFilters.OrderProcedureIds,
					AppointmentReportFilters.OrderProcedureGroupIds,
					AppointmentReportFilters.OrderHasProcedureGroup,
				]) && <SectionTitle title="Services" />}
				{shouldShowFilter(AppointmentReportFilters.OrderProcedureIds)
					&& <span data-param="orderProcedureIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.orderProcedureIds || 'orderProcedureIds'}
							type="procedureList"
							onChange={(value: any) => handleChange({ orderProcedureIds: value })}
							placeholder="Services..."
							loadOnFocus
							antdProps={{
								defaultValue: values?.orderProcedures || [],
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.orderProcedures || []}
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.OrderProcedureGroupIds)
					&& <span data-param="orderProcedureGroupIds" className="multi-select-filter filter-element">
						<ProcedureGroupsFilter
							key="orderProcedureGroupIds"
							value={filters.orderProcedureGroupIds}
							onChange={handleChange}
							withAdditionalOptions
							procedureGroups={values?.procedureGroups}
							antdProps={{ getPopupContainer: getSelectFilterContainer }}
							valueName="orderProcedureGroupIds"
						/>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.OrderGlobalProcedureIds)
					&& <div className="filter-element multi-select-filter" data-param="orderGlobalProcedureIds">
						<MultiSelectAuto
							key={keys.orderGlobalProceduresList || keys.key}
							type="globalProcedureViewList"
							renderTitle={(item: GlobalProcedure) => item.name}
							placeholder="Global Services..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ orderGlobalProcedureIds: value.map((q) => +q) });
							}}
							items={values?.orderGlobalProcedures}
							antdProps={{
								defaultValue: values?.orderGlobalProcedures || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderHasProcedureGroup)
					&& <span data-param="orderHasProcedureGroup" className="responsive-radio responsive-radio__short">
						<Radio.Group
							key={keys.orderHasProcedureGroup}
							className="pull-right"
							name="orderHasProcedureGroup"
							value={filters.orderHasProcedureGroup ?? null}
							onChange={(e) => {
								handleChange({ orderHasProcedureGroup: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value title="Has Service Group">
								<i className="fa fa-object-group" aria-hidden="true" />
							</Radio.Button>
							<Radio.Button value={false} title="No Service Group">
								<i className="fa fa-object-group" aria-hidden="true" />
								<i className="fa fa-times times-icon" />
							</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.HasDoctorProcedure)
				&& <span data-param="hasDoctorProcedure" className="responsive-radio responsive-radio__short">
					<Radio.Group
						key={keys.hasDoctorProcedure}
						className="pull-right"
						name="hasDoctorProcedure"
						value={filters.hasDoctorProcedure ?? null}
						onChange={(e) => {
							handleChange({ hasDoctorProcedure: e.target.value ?? undefined });
						}}
					>
						<Radio.Button value title="Has Doctor Procedure">
							<i className="fa fa-user-md" aria-hidden="true" />
						</Radio.Button>
						<Radio.Button value={false} title="No Doctor Procedure">
							<i className="fa fa-user-md" aria-hidden="true" />
							<i className="fa fa-times times-icon" />
						</Radio.Button>
						<Radio.Button value={null}>All</Radio.Button>
					</Radio.Group>
				</span>}
				{shouldShowFilter([
					AppointmentReportFilters.OrderDoctorIds,
					AppointmentReportFilters.OrderProfessionIds,
					AppointmentReportFilters.OrderSpecialtyIds,
					AppointmentReportFilters.OrderDisabled,
				]) && <SectionTitle title="Providers" />}
				{shouldShowFilter(AppointmentReportFilters.OrderDoctorIds)
					&& <div data-param="orderDoctorIds" className="multi-select-filter filter-element">
						<DoctorAutocompleteFilter
							multiple
							key={keys.orderDoctorId}
							onChange={(value: any) => handleChange({ orderDoctorIds: value })}
							items={values?.orderDoctors || []}
							antdProps={{
								defaultValue: values?.orderDoctors || [],
								allowClear: true,
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderProfessionIds)
					&& <div className="filter-element multi-select-filter" data-param="orderProfessionIds">
						<MultiSelectAuto
							key={keys.orderProfessionIds}
							type="professionList"
							renderTitle={(item: Profession) => item.nameEn}
							placeholder="Providers Professions..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ orderProfessionIds: value.map((q) => +q) });
							}}
							items={values?.professions}
							antdProps={{
								defaultValue: values?.orderProfessions || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.OrderSpecialtyIds)
					&& <div className="filter-element multi-select-filter" data-param="orderSpecialtyIds">
						<MultiSelectAuto
							key={keys.orderSpecialtyIds || keys.key}
							type="specialtyViewList"
							renderTitle={(item: Specialty) => item.name}
							placeholder="Specialties..."
							loadOnFocus
							onChange={(value: any) => {
								handleChange({ orderSpecialtyIds: value.map((q) => +q) });
							}}
							items={values?.orderSpecialties}
							antdProps={{
								defaultValue: values?.orderSpecialties || [],
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderDisabled)
					&& <div
						className="responsive-radio"
						data-param="orderDisabled"
						style={{ flex: `1 1 calc(${orderDisabledWidth}% - 20px)`, maxWidth: 'calc(33.3% - 20px)' }}
					>
						<DisabledFilter
							withIcons
							allAsText
							className="pull-right custom-radio"
							value={filters.orderDisabled}
							onChange={(value) => {
								handleChange({ orderDisabled: value });
							}}
						/>
					</div>}
				{shouldShowFilter([
					AppointmentReportFilters.OrderDiseaseId,
					AppointmentReportFilters.OrderPayerId,
					AppointmentReportFilters.OrderUserIds,
					AppointmentReportFilters.OrderPriorityFiltering,
					AppointmentReportFilters.InsuranceCreditNormal,
					AppointmentReportFilters.SubmittedOrderId,
					AppointmentReportFilters.WithTips,
					AppointmentReportFilters.HasDoctorPayer,
					AppointmentReportFilters.TargetType,
					AppointmentReportFilters.OrderSystemType,
				]) && <SectionTitle title="System" />}
				{shouldShowFilter(AppointmentReportFilters.OrderDiseaseId) && <AutocompleteFilter<Disease>
					key={keys.orderDiseaseId || 'orderDiseaseId'}
					type="diseaseList"
					renderTitle="name"
					handleChangeAndRefresh={handleChange}
					param="orderDiseaseId"
					value={values?.orderDiseaseName ?? ''}
					placeholder="Medical Questions..."
					loadOnFocus
				/>}
				{shouldShowFilter(AppointmentReportFilters.OrderPayerId) && <AutocompleteFilter<Payer>
					key={keys.orderPayerId || 'orderPayerId'}
					type="payerViewList"
					renderTitle={(item: Payer) => {
						return `${item.code} ${item.name}`;
					}}
					handleChangeAndRefresh={handleChange}
					param="orderPayerId"
					value={values?.orderPayerName ?? ''}
					placeholder="Payer..."
					loadOnFocus
				/>}
				{shouldShowFilter(AppointmentReportFilters.OrderUserIds)
					&& <div data-param="orderUserIds" className="multi-select-filter filter-element">
						<MultiSelectAuto
							key={keys.orderUserIds || 'orderUserIds'}
							type="userAutoCompleteViewList"
							onChange={(value: any) => handleChange({ orderUserIds: value })}
							placeholder="Users..."
							loadOnFocus
							antdProps={{
								defaultValue: (values?.orderUsers || []) as any,
								maxTagCount: 1,
								getPopupContainer: getSelectFilterContainer,
							} as any}
							items={values?.orderUsers}
							renderTitle={(item: User) => `${item.firstName} ${item.lastName}`}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OrderPriorityFiltering)
					&& <div
						data-param="orderPriorityFiltering"
						className="responsive-radio"
						style={{ flex: '1 1 calc(12% - 20px)', maxWidth: 'calc(33.3% - 20px)' }}
					>
						<Radio.Group
							key={keys.orderPriorityFiltering}
							className="pull-right custom-radio"
							name="orderPriorityFiltering"
							value={!!filters?.orderPriorityFiltering}
							onChange={(e) => {
								handleChange({ orderPriorityFiltering: e.target.value });
							}}
						>
							<Radio.Button value title="Order filtering priority">
								<i className="fa fa-money" aria-hidden="true" />
							</Radio.Button>
							<Radio.Button value={false} title="Without Order filtering priority">
								<i className="fa fa-calendar" aria-hidden="true" />
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceCreditNormal)
					&& <div
						data-param="withInsurance"
						className="btn-group ratio-group ratio-group_small responsive-radio"
						role="group"
					>
						<span data-param="withInsurance" className="filter-ratio-element">
							<button
								type="button"
								className={`${filters.withInsurance ? 'btn btn-primary' : 'btn btn-light'}`}
								onClick={() => {
									handleChange({ withInsurance: !filters.withInsurance });
								}}
							>
								Insurance
							</button>
						</span>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceCreditNormal)
					&& <div
						data-param="withCredit"
						className="btn-group ratio-group ratio-group_small responsive-radio"
						role="group"
					>
						<span data-param="withCredit" className="filter-ratio-element">
							<button
								type="button"
								className={`${filters.withCredit ? 'btn btn-primary' : 'btn btn-light'}`}
								onClick={() => {
									handleChange({ withCredit: !filters.withCredit });
								}}
							>
								Credit
							</button>
						</span>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceCreditNormal)
					&& <div
						data-param="withNormal"
						className="btn-group ratio-group ratio-group_small responsive-radio"
						role="group"
					>
						<span data-param="withNormal" className="filter-ratio-element">
							<button
								type="button"
								className={`${filters.withNormal ? 'btn btn-primary' : 'btn btn-light'}`}
								onClick={() => {
									handleChange({ withNormal: !filters.withNormal });
								}}
							>
								Normal
							</button>
						</span>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.SubmittedOrderId)
					&& <span
						data-param="withSubmittedOrderId"
						className="responsive-radio long-radio"
						style={{ maxWidth: 'calc(33.33% - 20px)' }}
					>
						<Radio.Group
							key={keys.withSubmittedOrderId}
							className="pull-right"
							name="withSubmittedOrderId"
							value={filters.withSubmittedOrderId !== undefined ? filters.withSubmittedOrderId : null}
							onChange={(e) => {
								handleChange({ withSubmittedOrderId: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>With Quotes</Radio.Button>
							<Radio.Button value={false}>Without Quotes</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.HasDoctorPayer)
					&& <span data-param="hasDoctorPayer" className="responsive-radio long-radio" style={{ maxWidth: 'calc(33.33% - 20px)' }}>
						<Radio.Group
							key={keys.hasDoctorPayer}
							className="pull-right"
							name="hasDoctorPayer"
							value={filters.hasDoctorPayer ?? null}
							onChange={(e) => {
								handleChange({ hasDoctorPayer: e.target.value ?? undefined });
							}}
						>
							<Radio.Button value>Has Doctor Payer</Radio.Button>
							<Radio.Button value={false}>No Doctor Payer</Radio.Button>
							<Radio.Button value={null}>All</Radio.Button>
						</Radio.Group>
					</span>}
				{shouldShowFilter(AppointmentReportFilters.WithTips) && <span data-param="withTips" className="filter-ratio-element">
					<Radio.Group
						key={keys.withTips}
						className="pull-right ml10 mb10"
						name="withTips"
						value={filters.withTips || false}
						onChange={(e) => {
							handleChange({ withTips: e.target.value });
						}}
					>
						<Radio.Button value>With Tips</Radio.Button>
						<Radio.Button value={false}>Without Tips</Radio.Button>
					</Radio.Group>
				</span>}
				{shouldShowFilter(AppointmentReportFilters.TargetType) && <span data-param="targetType" className="responsive-radio">
					<Radio.Group
						key={keys.targetType}
						className="pull-right"
						name="targetType"
						value={filters.targetType ?? TargetType.All}
						onChange={(e) => {
							handleChange({ targetType: e.target.value });
						}}
					>
						<Radio.Button value={TargetType.Targets}>Service</Radio.Button>
						<Radio.Button value={TargetType.Payment}>Payment</Radio.Button>
						<Radio.Button value={TargetType.All}>All</Radio.Button>
					</Radio.Group>
				</span>}
				{shouldShowFilter(AppointmentReportFilters.OrderSystemType)
					&& <div data-param="orderSystemType" className="responsive-radio" style={{ flex: '0 0 calc(33.33% - 20px)' }}>
						<SystemTypeGroupFilter
							className=" "
							systemType={filters.orderSystemType}
							handleChangeAndRefresh={handleChange}
							paramName="orderSystemType"
						/>
					</div>}
				{shouldShowFilter([
					AppointmentReportFilters.RelatedSpecialsIds,
					AppointmentReportFilters.RelatedProcedureIds,
					AppointmentReportFilters.RelatedProductIds,
				]) && <SectionTitle title="Related" />}
				{shouldShowFilter(AppointmentReportFilters.RelatedProcedureIds)
				&& <span data-param="relatedProcedureIds" className="multi-select-filter filter-element">
					<MultiSelectAuto
						key={keys.relatedProcedureIds || 'relatedProcedureIds'}
						type="procedureList"
						onChange={(value: any) => handleChange({ relatedProcedureIds: value })}
						placeholder="Related Services..."
						loadOnFocus
						antdProps={{
							defaultValue: values?.relatedProcedures || [],
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.relatedProcedures || []}
					/>
				</span>
				}
				{shouldShowFilter(AppointmentReportFilters.RelatedProductIds)
				&& <span data-param="relatedProductIds" className="multi-select-filter filter-element">
					<MultiSelectAuto
						key={keys.relatedProductIds || 'relatedProductIds'}
						type="productList"
						onChange={(value: any) => handleChange({ relatedProductIds: value })}
						placeholder="Related Products..."
						loadOnFocus
						antdProps={{
							defaultValue: (values?.relatedProducts || []) as any,
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.relatedProducts}
					/>
				</span>}
				{shouldShowFilter(AppointmentReportFilters.RelatedSpecialsIds)
				&& <span data-param="relatedSpecialsIds" className="multi-select-filter filter-element">
					<MultiSelectAuto
						key={keys.relatedSpecialsIds || 'relatedSpecialsIds'}
						type="specialsAutocompleteList"
						onChange={(value: any) => handleChange({ relatedSpecialsIds: value })}
						placeholder="Related Specials..."
						loadOnFocus
						antdProps={{
							defaultValue: (values?.relatedSpecials || []) as any,
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.relatedSpecials}
					/>
				</span>}
				{/* --- End Order Filters ---*/}
				{/* --- Start Inner Additional Order Filters --- */}
				{!isUndefined(additionalTabsFilters?.[FilterTabOrderKey]) && innerKeyTabFilters[FilterTabOrderKey] && <>
					{(additionalTabsTitles?.[FilterTabOrderKey] === undefined || additionalTabsTitles?.[FilterTabOrderKey])
						&& <SectionTitle title={additionalTabsTitles?.[FilterTabOrderKey] || 'Additional Filters'} />}
					{transformChildrenList(innerKeyTabFilters[FilterTabOrderKey], filters, filtersHandler)}
				</>}
				<div data-param={additionalParams[FilterTabOrderKey]} style={{ display: 'none' }} />
				{/* --- End Inner Additional Order Filters --- */}
			</>,
		},
		{
			key: FilterTabPatientKey,
			label: <>
				<i className="fa fa-user" />
				<span className="tab-text">
					{' '}
					Patient
				</span>
			</>,
			className: 'filters-container',
			visible: shouldShowFilter([
				AppointmentReportFilters.PatientId,
				AppointmentReportFilters.ParentId,
				AppointmentReportFilters.ChildId,
				AppointmentReportFilters.Gender,
				AppointmentReportFilters.HasInsurance,
				AppointmentReportFilters.DebtMinMax,
				AppointmentReportFilters.AgeMinMax,
				AppointmentReportFilters.BalanceMinMax,
				AppointmentReportFilters.AppointmentsMinMax,
				AppointmentReportFilters.EmailsMinMax,
				AppointmentReportFilters.EthnicityId,
				AppointmentReportFilters.BirthDate,
			]),
			children: <>
				{shouldShowFilter([
					AppointmentReportFilters.EthnicityId,
					AppointmentReportFilters.PatientId,
					AppointmentReportFilters.ParentId,
					AppointmentReportFilters.ChildId,
					AppointmentReportFilters.Gender,
					AppointmentReportFilters.HasInsurance,
					AppointmentReportFilters.Allergies,
					AppointmentReportFilters.BirthDate,
				]) && <SectionTitle title="General" />}
				{shouldShowFilter(AppointmentReportFilters.PatientId) ? <AutocompleteFilter<Patient>
					key={keys.patientId || 'patientId'}
					type="patientViewList"
					renderTitle={getUserNameOrUnnamedWithEmail}
					handleChangeAndRefresh={handleChange}
					param="patientId"
					value={values?.patientName ?? ''}
					placeholder="Patient..."
					loadOnFocus
					antdProps={{
						getPopupContainer: getSelectFilterContainer,
					}}
				/> : <></>}
				{shouldShowFilter(AppointmentReportFilters.Gender) && <div className="filter-element" data-param="gender">
					<SelectFilter
						additionalParams={filters}
						param="gender"
						onChange={handleChange}
						defaultValue="All Genders"
						currentValue={GenderNames[filters.gender ?? -1] || ''}
					>
						<Option value="">All Genders</Option>
						<Option value={Gender.None}>None</Option>
						<Option value={Gender.Female}>Female</Option>
						<Option value={Gender.Male}>Male</Option>
					</SelectFilter>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.EthnicityId) && <div className="filter-element" data-param="ethnicityId">
					<EthnicitySelect
						additionalParams={{ ethnicityId: filters.ethnicityId || -1 }}
						onChange={(value) => handleChange({ ethnicityId: value })}
						initEthnicities={initialValues?.ethnicities}
						getPopupContainer={getSelectFilterContainer}
						additionalOption={<Option value={-2}>Without Ethnicity</Option>}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientZip) ? <AutocompleteFilter<ZipCode>
					key={keys.patientZip || 'patientZip'}
					type="zipCodeList"
					renderTitle={(item) => item.zip}
					handleChangeAndRefresh={((_, option) => {
						handleChange({
							patientZip: option?.item?.zip
								? `z${option?.item?.zip}`
								: undefined,
						});
					})}
					param="patientZip"
					value={filters?.patientZip?.replace('z', '') ?? ''}
					placeholder="Zip..."
					loadOnFocus
					antdProps={{
						getPopupContainer: getSelectFilterContainer,
					}}
				/> : <></>}
				{shouldShowFilter(AppointmentReportFilters.Allergies) ? <div className="filter-element multi-select-filter">
					<Select
						getPopupContainer={getSelectFilterContainer}
						className={className ?? 'pull-right ml10 multiple-select-filter'}
						mode="multiple"
						placeholder="Allergies..."
						value={filters.allergies?.filter((q) => q).map((q) => q.toString()) || []}
						onChange={(value: any) => {
							handleChange({ allergies: value });
						}}
						maxTagCount={2}
						maxTagTextLength={10}
						showSearch={{ filterOption: false }}
						allowClear
					>
						{Object.keys(AllergyTypeName).map((value) =>
							<Option value={value} key={+value}>{AllergyTypeName[value]}</Option>)}
					</Select>
				</div> : <></>}
				{shouldShowFilter(AppointmentReportFilters.Vitals) ? <div className="filter-element multi-select-filter" data-param="vitalIds">
					<VitalsFilter
						key="vitalIds"
						value={filters.vitalIds}
						onChange={handleChange}
						withAdditionalOptions
						vitals={values?.vitals}
						antdProps={{ getPopupContainer: getSelectFilterContainer }}
						valueName="vitalIds"
					/>
				</div> : <></>}
				{shouldShowFilter(AppointmentReportFilters.ParentId) ? <AutocompleteFilter<Patient>
					key={keys.parentId || 'parentId'}
					type="patientViewList"
					renderTitle={getUserNameOrUnnamedWithEmail}
					handleChangeAndRefresh={(e) => {
						handleChange({
							parentId: e.currentTarget.value as number,
							childId: undefined,
						});
						setKeys((prev: any) => ({
							...prev,
							childId: Math.random(),
						}));
					}}
					param="parentId"
					value={values?.parentName ?? ''}
					placeholder="Parent..."
					loadOnFocus
					params={{
						patientParentType: PatientParentType.Parent,
					}}
					antdProps={{
						getPopupContainer: getSelectFilterContainer,
					}}
				/> : <></>}
				{shouldShowFilter(AppointmentReportFilters.ChildId) ? <AutocompleteFilter<Patient>
					key={keys.childId || 'childId'}
					type="patientViewList"
					renderTitle={getUserNameOrUnnamedWithEmail}
					handleChangeAndRefresh={(e) => {
						handleChange({
							childId: e.currentTarget.value as number,
							parentId: undefined,
						});
						setKeys((prev: any) => ({
							...prev,
							parentId: Math.random(),
						}));
					}}
					param="childId"
					value={values?.childName ?? ''}
					placeholder="Child..."
					loadOnFocus
					params={{
						patientParentType: PatientParentType.Child,
					}}
					antdProps={{
						getPopupContainer: getSelectFilterContainer,
					}}
				/> : <></>}
				{shouldShowFilter(AppointmentReportFilters.BirthDate) && <div data-param="birthDate" className="col-4">
					<DatePickerReact
						key={keys.birthDate || 'birthDate'}
						value={filters.birthDate ?? null}
						placeholder="Birth Date..."
						datePickerProps={{
							id: 'birthDate',
							showMonthDropdown: true,
							showYearDropdown: true,
							dropdownMode: 'select',
							autoComplete: 'off',
							minDate: moment().add(-100, 'years').toDate(),
							maxDate: new Date(),
						}}
						onChange={(date) => handleChange({ birthDate: date ?? undefined })}
						utc
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientParentType)
				&& <div data-param="patientParentType" className="filter-radio-elastic" style={{ maxWidth: 'calc(33.3% - 20px)' }}>
					<Radio.Group
						className=""
						name="patientParentType"
						value={filters.patientParentType !== undefined ? +filters.patientParentType : 0}
						onChange={(e) => handleChange({ patientParentType: e.target.value })}
					>
						{patientParentTypeGroup.map((item) =>
							<Radio.Button value={item.value} key={item.value}>{item.caption}</Radio.Button>)}
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.HasEmail)
				&& <div data-param="hasEmail" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="hasEmail"
						value={filters.hasEmail !== undefined ? filters.hasEmail : null}
						onChange={(e) => handleChange({ hasEmail: e.target.value })}
					>
						<Radio.Button value key={1}>Email</Radio.Button>
						<Radio.Button value={false} key={2}>No Email</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.HasCellPhone)
				&& <div data-param="hasCellPhone" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="hasCellPhone"
						value={filters.hasCellPhone !== undefined ? filters.hasCellPhone : null}
						onChange={(e) => handleChange({ hasCellPhone: e.target.value })}
					>
						<Radio.Button value key={1}>Phone</Radio.Button>
						<Radio.Button value={false} key={2}>No Phone</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.SystemType)
				&& <div data-param="patientSystemType" className="responsive-radio" style={{ flex: '0 0 calc(33.33% - 20px)' }}>
					<SystemTypeGroupFilter
						className=" "
						systemType={filters.patientSystemType}
						handleChangeAndRefresh={handleChange}
						paramName="patientSystemType"
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.HasInsurance)
					&& <div
						data-param="patientHasInsurance"
						className="responsive-radio"
						style={{ flex: '1 1 calc(18% - 20px)', maxWidth: 'calc(33.3% - 20px)' }}
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="patientHasInsurance"
							value={filters.patientHasInsurance !== undefined ? filters.patientHasInsurance : null}
							onChange={(e) => handleChange({ patientHasInsurance: e.target.value })}
						>
							<Radio.Button value title="Insurance">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-shield" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Without Insurance">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-shield" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.HasDrivingLicense)
				&& <div
					data-param="patientHasDrivingLicense"
					className="responsive-radio"
					style={{ flex: '1 1 calc(18% - 20px)', maxWidth: 'calc(33.3% - 20px)' }}
				>
					<Radio.Group
						className="pull-right custom-radio"
						name="patientHasDrivingLicense"
						value={filters.patientHasDrivingLicense !== undefined ? filters.patientHasDrivingLicense : null}
						onChange={(e) => handleChange({ patientHasDrivingLicense: e.target.value })}
					>
						<Radio.Button value title="Driving License">
							<div className="custom-radio-button">
								<i className="tab-icon fa fa-car" aria-hidden="true" />
							</div>
						</Radio.Button>
						<Radio.Button value={false} title="Without Driving License">
							<div className="custom-radio-button">
								<i className="tab-icon fa fa-car" aria-hidden="true" />
								<i className="fa fa-times times-icon" />
							</div>
						</Radio.Button>
						<Radio.Button value={null} className="custom-radio-button">
							All
						</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.IsEmailValid)
					&& <div
						data-param="isEmailValid"
						className="responsive-radio"
						style={{ flex: '1 1 calc(18% - 20px)', maxWidth: 'calc(33.3% - 20px)' }}
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="isEmailValid"
							value={filters.isEmailValid !== undefined ? filters.isEmailValid : null}
							onChange={(e) => handleChange({ isEmailValid: e.target.value })}
						>
							<Radio.Button value title="Valid Email Addresses">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-envelope-o" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Not Valid Email Addresses">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-envelope-o" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.HasValidDisclosure)
					&& <div
						data-param="hasValidDisclosure"
						className="responsive-radio"
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="hasValidDisclosure"
							value={filters.hasValidDisclosure !== undefined ? filters.hasValidDisclosure : null}
							onChange={(e) => handleChange({ hasValidDisclosure: e.target.value })}
						>
							<Radio.Button value title="Patient With Valid Disclosure">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-flag" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Patient Without Valid Disclosure">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-flag" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.HasImportantFlag)
					&& <div
						data-param="hasImportantFlag"
						className="responsive-radio"
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="hasImportantFlag"
							value={filters.hasImportantFlag !== undefined ? filters.hasImportantFlag : null}
							onChange={(e) => handleChange({ hasImportantFlag: e.target.value })}
						>
							<Radio.Button value title="Patient With Important Comments">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-exclamation-circle red-text" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Patient Without Important Comments">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-exclamation-circle red-text" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.CompanyTemplateExpired)
					&& <div
						data-param="companyTemplateExpired"
						className="responsive-radio"
					>
						<Radio.Group
							className="pull-right custom-radio"
							name="companyTemplateExpired"
							value={filters.companyTemplateExpired !== undefined ? filters.companyTemplateExpired : null}
							onChange={(e) => handleChange({ companyTemplateExpired: e.target.value })}
						>
							<Radio.Button value title="Patient With Expired Company Template">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-file-text-o" aria-hidden="true" />
								</div>
							</Radio.Button>
							<Radio.Button value={false} title="Patient Without Expired Company Template">
								<div className="custom-radio-button">
									<i className="tab-icon fa fa-file-text-o" aria-hidden="true" />
									<i className="fa fa-times times-icon" />
								</div>
							</Radio.Button>
							<Radio.Button value={null} className="custom-radio-button">
								All
							</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.EmailConfirmed)
					&& <div data-param="emailConfirmed" className="filter-radio-elastic col-4">
						<Radio.Group
							className="pull-right ml10"
							name="emailConfirmed"
							value={filters.emailConfirmed !== undefined ? filters.emailConfirmed : null}
							onChange={(e) => handleChange({ emailConfirmed: e.target.value })}
						>
							<Radio.Button value key={1}>Email Confirmed</Radio.Button>
							<Radio.Button value={false} key={2}>Email Not Confirmed</Radio.Button>
							<Radio.Button value={null} key={0}>All</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.PhoneNumberConfirmed)
					&& <div data-param="phoneNumberConfirmed" className="filter-radio-elastic col-4">
						<Radio.Group
							className="pull-right ml10"
							name="phoneNumberConfirmed"
							value={filters.phoneNumberConfirmed !== undefined ? filters.phoneNumberConfirmed : null}
							onChange={(e) => handleChange({ phoneNumberConfirmed: e.target.value })}
						>
							<Radio.Button value key={1}>Phone Confirmed</Radio.Button>
							<Radio.Button value={false} key={2}>Phone Not Confirmed</Radio.Button>
							<Radio.Button value={null} key={0}>All</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientType)
					&& <div data-param="patientType" className="filter-radio-elastic col-4">
						<Radio.Group
							className="pull-right ml10"
							name="patientType"
							value={filters.patientType !== undefined ? filters.patientType : null}
							onChange={(e) => handleChange({ patientType: e.target.value })}
						>
							<Radio.Button value={PatientType.New} key={1}>New</Radio.Button>
							<Radio.Button value={PatientType.Returning} key={2}>Returning</Radio.Button>
							<Radio.Button value={null} key={0}>All</Radio.Button>
						</Radio.Group>
					</div>}
				{shouldShowFilter([
					AppointmentReportFilters.DebtMinMax,
					AppointmentReportFilters.AgeMinMax,
					AppointmentReportFilters.BalanceMinMax,
					AppointmentReportFilters.AppointmentsMinMax,
					AppointmentReportFilters.EmailsMinMax,
				]) && <SectionTitle title="Numbers section" />}

				{shouldShowFilter(AppointmentReportFilters.BalanceMinMax)
				&& <div className="filter-range-element" data-param={['balanceMin', 'balanceMax']}>
					<RangeInputs
						key={keys.key}
						placeholder="Balance"
						onChange={handleChange}
						valueName="balance"
						filters={filters}
						hasMin={false}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.AgeMinMax)
				&& <div className="filter-range-element" data-param={['minAge', 'maxAge']}>
					<RangeInputs
						key={keys.key}
						placeholder="Age"
						onChange={handleChange}
						valueName=""
						filters={filters}
						firstValueName="minAge"
						secondValueName="maxAge"
						hasMin={false}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.AppointmentsMinMax)
				&& <div className="filter-range-element" data-param={['minAppointments', 'maxAppointments']}>
					<RangeInputs
						key={keys.key}
						placeholder="Appt."
						onChange={handleChange}
						valueName=""
						filters={filters}
						firstValueName="minAppointments"
						secondValueName="maxAppointments"
						hasMin={false}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.EmailsMinMax)
				&& <div className="filter-range-element" data-param={['minEmails', 'maxEmails']}>
					<RangeInputs
						key={keys.key}
						placeholder="Emails"
						onChange={handleChange}
						valueName="emails"
						filters={filters}
						firstValueName="minEmails"
						secondValueName="maxEmails"
						hasMin={false}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.DebtMinMax)
				&& <div className="filter-range-element filter-range-element_width25" data-param={['debtMin', 'debtMax']}>
					<RangeInputs
						key={keys.key}
						placeholder="Debt"
						onChange={handleChange}
						valueName="debt"
						filters={filters}
						hasMin={false}
					/>
				</div>}

				{shouldShowFilter(AppointmentReportFilters.OrdersMinMax)
				&& <div className="filter-range-element filter-range-element_width25" data-param={['minOrders', 'maxOrders']}>
					<RangeInputs
						key={keys.key}
						placeholder="Orders"
						onChange={handleChange}
						valueName=""
						firstValueName="minOrders"
						secondValueName="maxOrders"
						filters={filters}
						hasMin={false}
					/>
				</div>}

				{shouldShowFilter(AppointmentReportFilters.PhotosMinMax)
				&& <div className="filter-range-element filter-range-element_width25" data-param={['minPhotos', 'maxPhotos']}>
					<RangeInputs
						key={keys.key}
						placeholder="Photos"
						onChange={handleChange}
						valueName=""
						firstValueName="minPhotos"
						secondValueName="maxPhotos"
						filters={filters}
						hasMin={false}
					/>
				</div>}

				{shouldShowFilter(AppointmentReportFilters.FilesMinMax)
				&& <div className="filter-range-element filter-range-element_width25" data-param={['minFiles', 'maxFiles']}>
					<RangeInputs
						key={keys.key}
						placeholder="Files"
						onChange={handleChange}
						valueName=""
						firstValueName="minFiles"
						secondValueName="maxFiles"
						filters={filters}
						hasMin={false}
					/>
				</div>}

				{shouldShowFilter([
					AppointmentReportFilters.DefaultDoctorId,
				]) && <SectionTitle title="System Settings" />}
				{shouldShowFilter(AppointmentReportFilters.DefaultDoctorId)
				&& <div className="filter-element" data-param="defaultDoctorId">
					<DoctorAutocompleteFilter
						key={keys.defaultDoctorId || 'defaultDoctorId'}
						handleChangeAndRefresh={handleChange}
						param="defaultDoctorId"
						value={values?.defaultDoctorName || ''}
						placeholder="Default Provider..."
						multiSelectAutoProps={{
							getPopupContainer: getSelectFilterContainer,
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.DefaultLocationId)
				&& <div data-param="defaultLocationId" className="filter-element">
					<LocationSelect
						allValueName="All Default Locations"
						param="defaultLocationId"
						selectProps={{
							value: filters.defaultLocationId,
						}}
						onChange={(e) => handleChange(e)}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.IsPatientActive)
				&& <div data-param="isPatientActive" className="filter-radio-elastic" style={{ maxWidth: 'calc(33.3% - 20px)' }}>
					<Radio.Group
						className=""
						name="isPatientActive"
						value={filters.isPatientActive !== undefined ? +filters.isPatientActive : 0}
						onChange={(e) => handleChange({ isPatientActive: e.target.value })}
					>
						{activeFilterGroup.map((item) =>
							<Radio.Button value={item.value} key={item.value}>{item.caption}</Radio.Button>)}
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.HasCopyToId)
				&& <div data-param="hasCopyToId" className="filter-radio-elastic" style={{ maxWidth: 'calc(33.3% - 20px)' }}>
					<Radio.Group
						className=""
						name="push"
						value={filters.hasCopyToId !== undefined ? filters.hasCopyToId : null}
						onChange={(e) => handleChange({ hasCopyToId: e.target.value })}
					>
						<Radio.Button value key={1}>Copied</Radio.Button>
						<Radio.Button value={false} key={2}>Not copied</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.CallOnly)
					&& <div data-param="callOnly" className="filter-radio-elastic long-radio" style={{ maxWidth: 'calc(33.3% - 20px)' }}>
						<Radio.Group
							className=""
							name="callOnly"
							value={filters.callOnly !== undefined ? +filters.callOnly : 0}
							onChange={(e) => handleChange({ callOnly: e.target.value })}
						>
							{callOnlyFilterGroup.map((item) =>
								<Radio.Button value={item.value} key={item.value}>{item.caption}</Radio.Button>)}
						</Radio.Group>
					</div>}

				{shouldShowFilter([
					AppointmentReportFilters.Email,
					AppointmentReportFilters.SMS,
					AppointmentReportFilters.Push,
					AppointmentReportFilters.Call,
				]) && <SectionTitle title="Notification Settings" />}

				{shouldShowFilter(AppointmentReportFilters.Email)
				&& <div data-param="email" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="email"
						value={filters.email !== undefined ? filters.email : null}
						onChange={(e) => handleChange({ email: e.target.value })}
					>
						<Radio.Button value key={1}>Emails</Radio.Button>
						<Radio.Button value={false} key={2}>No Emails</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.SMS)
				&& <div data-param="sMS" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="sMS"
						value={filters.sMS !== undefined ? filters.sMS : null}
						onChange={(e) => handleChange({ sMS: e.target.value })}
					>
						<Radio.Button value key={1}>SMS</Radio.Button>
						<Radio.Button value={false} key={2}>No SMS</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.Push)
				&& <div data-param="push" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="push"
						value={filters.push !== undefined ? filters.push : null}
						onChange={(e) => handleChange({ push: e.target.value })}
					>
						<Radio.Button value key={1}>Push</Radio.Button>
						<Radio.Button value={false} key={2}>No Push</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.Call)
				&& <div data-param="call" className="filter-radio-elastic col-4">
					<Radio.Group
						className="pull-right ml10"
						name="call"
						value={filters.call !== undefined ? filters.call : null}
						onChange={(e) => handleChange({ call: e.target.value })}
					>
						<Radio.Button value key={1}>Call</Radio.Button>
						<Radio.Button value={false} key={2}>No Call</Radio.Button>
						<Radio.Button value={null} key={0}>All</Radio.Button>
					</Radio.Group>
				</div>}

				{shouldShowFilter([
					AppointmentReportFilters.InventoryProcedureIds,
					AppointmentReportFilters.InventoryProductIds,
					AppointmentReportFilters.InventorySpecialsIds,
					AppointmentReportFilters.Inventory,
				]) && <SectionTitle title="Inventory" />}

				{shouldShowFilter(AppointmentReportFilters.InventoryProcedureIds)
				&& <div className="filter-element multi-select-filter" data-param="inventoryProcedureIds">
					<MultiSelectAuto
						key={keys.inventoryProcedureIds || 'inventoryProcedureIds'}
						type="procedureList"
						onChange={(value: any) => handleChange({ inventoryProcedureIds: value })}
						placeholder="Procedures..."
						loadOnFocus
						antdProps={{
							defaultValue: values?.inventoryProcedures || [],
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.inventoryProcedures || []}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.InventoryProductIds)
				&& <div className="filter-element multi-select-filter" data-param="inventoryProductIds">
					<MultiSelectAuto
						key={keys.inventoryProductIds || 'inventoryProductIds'}
						type="productList"
						onChange={(value: any) => handleChange({ inventoryProductIds: value })}
						placeholder="Products..."
						loadOnFocus
						antdProps={{
							defaultValue: values?.inventoryProducts || [],
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.inventoryProducts || []}
						params={{
							inventory: true,
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.InventorySpecialsIds)
				&& <div className="filter-element multi-select-filter" data-param="inventorySpecialsIds">
					<MultiSelectAuto
						key={keys.inventorySpecialsIds || 'inventorySpecialsIds'}
						type="specialsAutocompleteList"
						onChange={(value: any) => handleChange({ inventorySpecialsIds: value })}
						placeholder="Specials..."
						loadOnFocus
						antdProps={{
							defaultValue: values?.inventorySpecials || [],
							maxTagCount: 1,
							getPopupContainer: getSelectFilterContainer,
						} as any}
						items={values?.inventorySpecials || []}
						params={{
							usable: true,
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.Inventory)
					&& <SelectFilter
						additionalParams={filters}
						param="inventory"
						onChange={handleChange}
						defaultValue={InventoryFiltersNames[0]}
						currentValue={+(filters.inventory || 0)}
					>
						{Object.keys(InventoryFiltersNames).map((key) => {
							return <Option value={+key} key={key}>
								{InventoryFiltersNames[key]}
							</Option>;
						})}
					</SelectFilter>}
				{shouldShowFilter([
					AppointmentReportFilters.InsurancePayer,
					AppointmentReportFilters.InsuranceType,
					AppointmentReportFilters.InsuranceId,
					AppointmentReportFilters.InsuranceGroupId,
					AppointmentReportFilters.RelationshipToInsured,
					AppointmentReportFilters.InsuranceCountMinMax,
					AppointmentReportFilters.InsuranceValidity,
					AppointmentReportFilters.PatientInsuranceStatus,
				]) && <SectionTitle title="Insurance" />}
				{shouldShowFilter(AppointmentReportFilters.InsurancePayer) && <AutocompleteFilter<Payer>
					key={keys.insurancePayerId || 'insurancePayerId'}
					type="payerViewList"
					renderTitle={(item: Payer) => {
						return `${item.code} ${item.name}`;
					}}
					handleChangeAndRefresh={handleChange}
					param="insurancePayerId"
					value={values?.insurancePayerName ?? ''}
					placeholder="Payer..."
					loadOnFocus
				/>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceType)
					&& <div className="filter-element" data-param="insuranceType">
						<SelectFilter
							additionalParams={filters}
							param="insuranceType"
							onChange={handleChange}
							defaultValue="Any Type Of Coverage"
							currentValue={TypeOfCoverageNames[filters.insuranceType ?? -1] || ''}
						>
							<Option value="">Any Type Of Coverage</Option>
							{Object.keys(TypeOfCoverageNames).map((key) => {
								return <Option value={+key} key={key}>
									{TypeOfCoverageNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.RelationshipToInsured)
					&& <div className="filter-element" data-param="relationshipToInsured">
						<SelectFilter
							additionalParams={filters}
							param="relationshipToInsured"
							onChange={handleChange}
							defaultValue="Any Relationship To Insured"
							currentValue={RelationShipToInsuredEnumNames[filters.relationshipToInsured ?? -1] || ''}
						>
							<Option value="">Any Relationship To Insured</Option>
							{Object.keys(RelationShipToInsuredEnumNames).map((key) => {
								return <Option value={+key} key={key}>
									{RelationShipToInsuredEnumNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.InsuranceValidity)
					&& <div className="filter-element" data-param="insuranceValidity">
						<SelectFilter
							additionalParams={filters}
							param="insuranceValidity"
							onChange={handleChange}
							defaultValue=""
							currentValue={filters.insuranceValidity}
						>
							<Option value="">Any Insurance Validity</Option>
							{Object.keys(PatientDocumentValidityNames).map((key) => {
								return <Option value={+key} key={key}>
									<ColorLine color={PatientDocumentValidityColors[key]} />
									{PatientDocumentValidityNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.PatientInsuranceStatus)
					&& <div className="filter-element" data-param="patientInsuranceStatus">
						<SelectFilter
							additionalParams={filters}
							param="patientInsuranceStatus"
							onChange={handleChange}
							defaultValue=""
							currentValue={filters.patientInsuranceStatus}
						>
							<Option value="">Any Patient Insurance Status</Option>
							{Object.keys(PatientInfoStatusNames).map((key) => {
								return <Option value={+key} key={key}>
									<ColorLine
										color={PatientInfoStatusColor[key]}
									/>
									{PatientInfoStatusNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.InsuranceId)
					&& <div className="simple-search-component" data-param="insuranceId">
						<SimpleSearchInput
							name="insuranceId"
							placeholder="Insurance Id"
							defaultValue={filters?.insuranceId}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceGroupId)
					&& <div className="simple-search-component" data-param="insuranceGroupId">
						<SimpleSearchInput
							name="insuranceGroupId"
							placeholder="Group Insurance Id"
							value={filters?.insuranceGroupId}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.InsuranceCountMinMax)
					&& <div className="filter-range-element" data-param={['minInsuranceCount', 'maxInsuranceCount']}>
						<RangeInputs
							key={keys.key}
							placeholder="Insurance Count"
							onChange={handleChange}
							valueName=""
							filters={filters}
							firstValueName="minInsuranceCount"
							secondValueName="maxInsuranceCount"
							hasMin={false}
						/>
					</div>
				}
				{shouldShowFilter([
					AppointmentReportFilters.RelationshipToLicenseHolder,
					AppointmentReportFilters.DrivingLicenseValidity,
					AppointmentReportFilters.PatientDrivingLicenseStatus,
					AppointmentReportFilters.DriverNumber,
					AppointmentReportFilters.DrivingLicenseCountMinMax,
				]) && <SectionTitle title="Driving License" />}
				{shouldShowFilter(AppointmentReportFilters.DriverNumber)
					&& <div className="simple-search-component" data-param="driverNumber">
						<SimpleSearchInput
							onSubmit={handleChange}
							name="driverNumber"
							placeholder="Driver Number"
							value={filters?.driverNumber}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.RelationshipToLicenseHolder)
					&& <div className="filter-element" data-param="relationshipToLicenseHolder">
						<SelectFilter
							additionalParams={filters}
							param="relationshipToLicenseHolder"
							onChange={handleChange}
							defaultValue="Any Relationship To License Holder"
							currentValue={RelationshipToLicenseHolderEnumNames[filters.relationshipToLicenseHolder ?? -1] || ''}
						>
							<Option value="">Any Relationship To License Holder</Option>
							{Object.keys(RelationshipToLicenseHolderEnumNames).map((key) => {
								return <Option value={+key} key={key}>
									{RelationshipToLicenseHolderEnumNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.DrivingLicenseValidity)
					&& <div className="filter-element" data-param="drivingLicenseValidity">
						<SelectFilter
							additionalParams={filters}
							param="drivingLicenseValidity"
							onChange={handleChange}
							defaultValue=""
							currentValue={filters.drivingLicenseValidity}
						>
							<Option value="">Any Driving License Validity</Option>
							{Object.keys(PatientDocumentValidityNames).map((key) => {
								return <Option value={+key} key={key}>
									<ColorLine color={PatientDocumentValidityColors[key]} />
									{PatientDocumentValidityNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.PatientDrivingLicenseStatus)
					&& <div className="filter-element" data-param="patientDrivingLicenseStatus">
						<SelectFilter
							additionalParams={filters}
							param="patientDrivingLicenseStatus"
							onChange={handleChange}
							defaultValue=""
							currentValue={filters.patientDrivingLicenseStatus}
						>
							<Option value="">Any Patient Driving License Status</Option>
							{Object.keys(PatientInfoStatusNames).map((key) => {
								return <Option value={+key} key={key}>
									<ColorLine
										color={PatientInfoStatusColor[key]}
									/>
									{PatientInfoStatusNames[key]}
								</Option>;
							})}
						</SelectFilter>
					</div>
				}
				{shouldShowFilter(AppointmentReportFilters.DrivingLicenseCountMinMax)
					&& <div className="filter-range-element" data-param={['minDrivingLicenseCount', 'maxDrivingLicenseCount']}>
						<RangeInputs
							key={keys.key}
							placeholder="License Count"
							onChange={handleChange}
							valueName=""
							filters={filters}
							firstValueName="minDrivingLicenseCount"
							secondValueName="maxDrivingLicenseCount"
							hasMin={false}
						/>
					</div>
				}
				{shouldShowFilter([
					AppointmentReportFilters.PatientFirstName,
					AppointmentReportFilters.PatientLastName,
					AppointmentReportFilters.PatientEmail,
					AppointmentReportFilters.PatientPhone,
				]) && <SectionTitle title="Additional" />}
				{shouldShowFilter(AppointmentReportFilters.PatientFirstName)
					&& <div className="simple-search-component" data-param="patientFirstName">
						<SimpleSearchInput
							onSubmit={handleChange}
							name="patientFirstName"
							placeholder="First Name"
							value={filters?.patientFirstName}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientLastName)
					&& <div className="simple-search-component" data-param="patientLastName">
						<SimpleSearchInput
							onSubmit={handleChange}
							name="patientLastName"
							placeholder="Last Name"
							value={filters?.patientLastName}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientEmail)
					&& <div className="simple-search-component" data-param="patientEmail">
						<SimpleSearchInput
							onSubmit={handleChange}
							name="patientEmail"
							placeholder="Email"
							value={filters?.patientEmail}
							className="form-control"
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.PatientPhone)
					&& <div className="simple-search-component" data-param="patientPhone">
						<PhoneSimpleSearch
							key={keys.patientPhone}
							dataParam="patientPhone"
							onSubmit={handleChange}
							placeholder="Phone"
							name="patientPhone"
							value={filters?.patientPhone}
							defaultValue={filters?.patientPhone}
							withoutForm
							className="form-control"
						/>
					</div>}
				{/* --- Start Inner Additional Patient Filters --- */}
				{!isUndefined(additionalTabsFilters?.[FilterTabPatientKey]) && innerKeyTabFilters[FilterTabPatientKey] && <>
					{(additionalTabsTitles?.[FilterTabPatientKey] === undefined || additionalTabsTitles?.[FilterTabPatientKey])
						&& <SectionTitle title={additionalTabsTitles?.[FilterTabPatientKey] || 'Additional Filters'} />}
					{transformChildrenList(innerKeyTabFilters[FilterTabPatientKey], filters, filtersHandler)}
				</>}
				<div data-param={additionalParams[FilterTabPatientKey]} style={{ display: 'none' }} />
				{/* --- End Inner Additional Patient Filters --- */}
			</>,
		},
		{
			key: FilterTabInquiryKey,
			label: <>
				<i className="fa fa-question-circle-o" />
				<span className="tab-text">
					{' '}
					Inquiry
				</span>
			</>,
			className: 'filters-container',
			visible: (!isUndefined(additionalTabsFilters?.[FilterTabInquiryKey]) && innerKeyTabFilters[FilterTabInquiryKey])
				|| shouldShowFilter([
					AppointmentReportFilters.InnerInquiryStatusSelect,
					AppointmentReportFilters.InnerInquiryLocationId,
				])
				|| (showFullInquiryFilters && shouldShowFilter([
					AppointmentReportFilters.InquiryDoctorId,
					AppointmentReportFilters.InquiryMainPageId,
					AppointmentReportFilters.InquiryPageId,
					AppointmentReportFilters.InquiryDate,
					AppointmentReportFilters.InquiryPath,
					AppointmentReportFilters.InquiryDevices,
					AppointmentReportFilters.InquiryHasAppointment,
					AppointmentReportFilters.InquirySystem,
				])),
			children: <>
				{((showFullInquiryFilters && shouldShowFilter([
					AppointmentReportFilters.InquiryDoctorId,
					AppointmentReportFilters.InquiryMainPageId,
					AppointmentReportFilters.InquiryPageId,
					AppointmentReportFilters.InquiryDate,
					AppointmentReportFilters.InquirySystem,
				])) || shouldShowFilter([
					AppointmentReportFilters.InnerInquiryLocationId,
					AppointmentReportFilters.InnerInquiryStatusSelect,
					AppointmentReportFilters.InquiryHasAppointment,
				])) && <SectionTitle title="General" />}
				{shouldShowFilter(AppointmentReportFilters.InnerInquiryLocationId)
				&& <div data-param="inquiryLocationId" className="filter-element-responsive">
					<LocationSelect
						getPopupContainer={getSelectFilterContainer}
						selectProps={{
							className: 'pull-right',
							value: filters.inquiryLocationId,
						}}
						param="inquiryLocationId"
						onChange={(evt) => {
							handleChange({
								inquiryLocationId: evt.currentTarget.value,
							});
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.InnerInquiryStatusSelect) && <SelectFilter
					className="filter-element-responsive"
					additionalParams={filters}
					param="inquiryStatus"
					onChange={handleChange}
					defaultValue={filters.inquiryStatus ?? ''}
					currentValue={filters.inquiryStatus === InquiryStatus.All ? '' : filters.inquiryStatus}
				>
					<Option key="all" value="">{InquiryStatusNames[InquiryStatus.All]}</Option>
					{InquiryStatusArray.filter((item) => item !== InquiryStatus.All)
						.map((key) => <Option key={key} value={+key}>{InquiryStatusNames[key]}</Option>)}
				</SelectFilter>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryDoctorId)
					&& <div key="inquiryDoctorId" className="filter-element" data-param="inquiryDoctorId">
						<DoctorAutocompleteFilter
							key={keys.inquiryDoctorId}
							onSelect={(value: any) => handleChange({ inquiryDoctorId: value })}
							onChange={(value: any) => !value && handleChange({ inquiryDoctorId: undefined })}
							value={filters?.inquiryDoctorId === withoutEntity ? 'Without Doctor' : values?.inquiryDoctor}
							withAdditionalOption
							antdProps={{
								defaultValue: values?.inquiryDoctor,
								allowClear: true,
								getPopupContainer: getSelectFilterContainer,
							} as any}
						/>
					</div>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryMainPageId)
					&& <div className="filter-element" data-param="mainPageId">
						<AutocompleteFilter<Page>
							key={keys.mainPageId || 'mainPageId'}
							type="pageList"
							value={values?.mainPageName || undefined}
							renderTitle={(item: Page) => item.name}
							handleChangeAndRefresh={handleChange}
							param="mainPageId"
							placeholder="Main page..."
							loadOnFocus
							getPopupContainer={getSelectFilterContainer}
							params={{
								pageType: PageType.SERVICE,
								root: true,
							}}
						/>
					</div>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryPageId)
					&& <div className="filter-element" data-param="pageId">
						<AutocompleteFilter<Page>
							key={keys.pageId || 'pageId'}
							type="pageList"
							value={values?.pageName || undefined}
							renderTitle={(item: Page) => item.name}
							handleChangeAndRefresh={handleChange}
							param="pageId"
							placeholder="Page..."
							disabled={!filters.mainPageId || filters.mainPageId <= 0}
							loadOnFocus
							getPopupContainer={getSelectFilterContainer}
							params={{
								pageType: PageType.SERVICE,
								parentId: filters.mainPageId,
							}}
						/>
					</div>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryDate)
					&& <div key="dateMin" className="filter-element" data-param="dateMin">
						<Rangepicker
							key={keys.dateMin || 'dataMin'}
							value={filters.dateMin && filters.dateMax ? [filters.dateMin, filters.dateMax] : null}
							onChange={(date) => {
								if (date) {
									handleChange({
										dateMin: date[0],
										dateMax: date[1],
									});
								} else {
									handleChange({
										dateMin: null,
										dateMax: null,
									});
								}
							}}
							antdProps={{
								showTime: isMobile,
								classNames: { popup: 'mobile-range-picker' },
								allowClear: true,
								getPopupContainer: rangePickerContainer,
							}}
							forceFormat
							format={rangePickerFormat}
							placeholderRange={['Start scheduled time', 'End scheduled time']}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.Gender) && <SelectFilter
					key={keys.gender}
					additionalParams={filters}
					param="inquiryGender"
					onChange={handleChange}
					defaultValue="All Genders"
					currentValue={GenderNames[filters.inquiryGender ?? -1] || ''}
				>
					<Option value="">All Genders</Option>
					<Option value={Gender.None}>None</Option>
					<Option value={Gender.Female}>Female</Option>
					<Option value={Gender.Male}>Male</Option>
				</SelectFilter>}
				{checkFeatureAccess(Feature.MultipleLanguages) && shouldShowFilter(AppointmentReportFilters.Gender) && <SelectFilter
					key={keys.gender}
					additionalParams={filters}
					param="inquiryLanguage"
					onChange={handleChange}
					defaultValue="All Languages"
					currentValue={LanguageNames[filters.inquiryLanguage ?? -1] || ''}
				>
					<Option value="">All Languages</Option>
					<Option value={Lang.En}>{LanguageNames[Lang.En]}</Option>
					<Option value={Lang.Es}>{LanguageNames[Lang.Es]}</Option>
				</SelectFilter>}
				{checkFeatureAccess([Feature.Portal, Feature.Site]) && shouldShowFilter(AppointmentReportFilters.InquirySystem)
					&& <div data-param="inquirySystem" className="responsive-radio responsive-radio__short">
						<Radio.Group
							className="pull-right custom-radio-button"
							value={filters.inquirySystem}
							onChange={(ev) => handleChange({ inquirySystem: ev.target.value })}
						>
							<Radio.Button value={InquirySystem.Portal}>
								<img src={tunaLogo} alt="Portal Inquiry" style={{ maxHeight: '75%' }} title="Sent From The Portal" />
							</Radio.Button>
							<Radio.Button value={InquirySystem.Site}>
								<img
									src={tunaLogo}
									alt="Site Inquiry"
									style={{ maxHeight: '75%', filter: 'grayscale(100%)' }}
									title="Sent From The Site"
								/>
							</Radio.Button>
							<Radio.Button value={undefined}>All</Radio.Button>
						</Radio.Group>
					</div>}
				{showFullInquiryFilters && shouldShowFilter([
					AppointmentReportFilters.InquiryPath,
					AppointmentReportFilters.InquiryDevices,
					AppointmentReportFilters.InquiryHasAppointment,
				]) && <SectionTitle title="System" />}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryPath)
				&& <div key="path" className="simple-search-component" data-param="path">
					<SimpleSearchInput
						className="form-control"
						name="path"
						placeholder="Search by path"
					/>
				</div>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryDevices)
					&& <div className="filter-element" data-param="device">
						<SelectFilter
							additionalParams={filters}
							param="device"
							onChange={(e) => handleChange({ device: e.currentTarget.value })}
							className="pull-right ml10 mb10"
							defaultValue=""
							currentValue={filters.device !== undefined ? filters.device : ''}
						>
							<Option value="">All devices</Option>
							{Object.keys(DeviceTitle).map((key) => (
								<Option
									key={key}
									title={DeviceTitle[key]}
									value={+key}
								>
									<i
										className={`fa mr10 ${DeviceIcon[key]}`}
									/>
									{DeviceTitle[key]}
								</Option>
							))}
						</SelectFilter>
					</div>}
				{showFullInquiryFilters && shouldShowFilter(AppointmentReportFilters.InquiryHasAppointment) && <div
					key="hasAppointment"
					data-param="hasAppointment"
					className="responsive-radio long-radio"
					style={{ maxWidth: 'calc(33.3% - 20px)', minWidth: '30%' }}
				>
					<Radio.Group
						className="pull-right"
						name="hasAppointment"
						value={filters.hasAppointment !== undefined ? filters.hasAppointment : null}
						onChange={(e) => {
							handleChange({
								currentTarget: {
									name: 'hasAppointment',
									value: e.target.value,
								},
							});
						}}
					>
						{hasAppointmentGroup.map((item) => (
							<Radio.Button value={item.value} key={item.caption}>{item.caption}</Radio.Button>
						))}
					</Radio.Group>
				</div>}
				{/* --- Start Inner Additional Inquiry Filters --- */}
				{!isUndefined(additionalTabsFilters?.[FilterTabInquiryKey]) && innerKeyTabFilters[FilterTabInquiryKey] && <>
					{(additionalTabsTitles?.[FilterTabInquiryKey] === undefined || additionalTabsTitles?.[FilterTabInquiryKey])
						&& <SectionTitle title={additionalTabsTitles?.[FilterTabInquiryKey] || 'Other Filters'} />}
					{transformChildrenList(innerKeyTabFilters[FilterTabInquiryKey], filters, filtersHandler)}
				</>}
				<div data-param={additionalParams[FilterTabInquiryKey]} style={{ display: 'none' }} />
				{/* --- End Inner Additional Inquiry Filters --- */}
			</>,
		},
		{
			key: FilterTabConsentFormKey,
			label: <>
				<i className="fa fa-check-circle-o" />
				<span className="tab-text">
					{' '}
					Consent Form
				</span>
			</>,
			className: 'filters-container',
			visible: shouldShowFilter(AppointmentReportFilters.TemplateFilter),
			children: <>
				{shouldShowFilter(AppointmentReportFilters.TemplateFilter) && <div className="system-filter" data-param="templateId">
					<TemplateAutocompleteFilter
						currentValue={filters.templateId ?? ''}
						key={keys.templateId || 'templateId'}
						handleChangeAndRefresh={handleChange}
						param="templateId"
						value={values?.templateName || ''}
						placeholder="Consent Forms..."
						params={{
							templateType: TemplateType.Consent,
						}}
						autocompleteProps={{
							getPopupContainer: getSelectFilterContainer,
						}}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.TemplateFilter) && <div
					data-param="timeMaxSignedTemplate"
				>
					<Rangepicker
						value={filters.timeMaxSignedTemplate && filters.timeMinSignedTemplate
							? [+filters.timeMinSignedTemplate, +filters.timeMaxSignedTemplate] : null}
						onChange={(date) => {
							handleChange({
								timeMaxSignedTemplate: date?.[1] || null,
								timeMinSignedTemplate: date?.[0] || null,
								...(date?.[1] ? { templateSigned: true } : {}),
							});
						}}
						antdProps={{
							showTime: isMobile,
							classNames: { popup: 'mobile-range-picker' },
							getPopupContainer: rangePickerContainer,
						}}
						forceFormat
						format={rangePickerFormat}
						placeholderRange={['Start date', 'End date']}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.TemplateFilter) && <div className="filter-ratio-element" data-param="templateSigned">
					<TemplateSignedFilter
						className="pull-right ml10 mb10 custom-radio"
						value={filters.templateSigned}
						onChange={(value) => {
							handleChange({
								templateSigned: value,
							});
						}}
						withIcons
						allAsText
					/>
				</div>}
				{/* --- Start Inner Additional Consent Form Filters --- */}
				{!isUndefined(additionalTabsFilters?.[FilterTabConsentFormKey]) && innerKeyTabFilters[FilterTabConsentFormKey] && <>
					{(additionalTabsTitles?.[FilterTabConsentFormKey] === undefined || additionalTabsTitles?.[FilterTabConsentFormKey])
					&& <SectionTitle title={additionalTabsTitles?.[FilterTabConsentFormKey] || 'Additional Filters'} />}
					{transformChildrenList(innerKeyTabFilters[FilterTabConsentFormKey], filters, filtersHandler)}
				</>}
				<div data-param={additionalParams[FilterTabConsentFormKey]} style={{ display: 'none' }} />
				{/* --- End Inner Additional Consent Form Filters --- */}
			</>,
		},
	];

	if (additionalTab) {
		const tab = additionalTab(filters, handleChange);

		if (tab) {
			filtersTabs.push(tab);
		}
	}

	const resetKeys = () => {
		setKeys((prev: any) => ({
			...prev,
			patientId: Math.random(),
			parentId: Math.random(),
			childId: Math.random(),
			birthDate: Math.random(),
			doctorId: Math.random(),
			orderDoctorId: Math.random(),
			diseaseId: Math.random(),
			orderDiseaseId: Math.random(),
			payerId: Math.random(),
			orderPayerId: Math.random(),
			procedureIds: Math.random(),
			orderProcedureIds: Math.random(),
			relatedProcedureIds: Math.random(),
			relatedProductIds: Math.random(),
			relatedSpecialsIds: Math.random(),
			appointmentTypeIds: Math.random(),
			orderAppointmentTypeIds: Math.random(),
			inventoryProductIds: Math.random(),
			inventorySpecialsIds: Math.random(),
			inventoryProcedureIds: Math.random(),
			userIds: Math.random(),
			orderUserIds: Math.random(),
			productIds: Math.random(),
			categoryIds: Math.random(),
			paymentType: Math.random(),
			disabled: Math.random(),
			objectHistoryType: Math.random(),
			roomIds: Math.random(),
			specialsIds: Math.random(),
			globalProcedureIds: Math.random(),
			orderGlobalProcedureIds: Math.random(),
			specialtyIds: Math.random(),
			orderSpecialtyIds: Math.random(),
			professionIds: Math.random(),
			orderProfessionIds: Math.random(),
			procedureGroupIds: Math.random(),
			orderProcedureGroupIds: Math.random(),
			globalProceduresList: Math.random(),
			orderGlobalProceduresList: Math.random(),
			templateId: Math.random(),
			mainPageId: Math.random(),
			pageId: Math.random(),
			inquiryDoctorId: Math.random(),
			path: Math.random(),
			key: Math.random(),
			defaultDoctorId: Math.random(),
			insurancePayerId: Math.random(),
			insuranceId: Math.random(),
			insuranceGroupId: Math.random(),
			patientFirstName: Math.random(),
			patientLastName: Math.random(),
			patientEmail: Math.random(),
			patientPhone: Math.random(),
			driverNumber: Math.random(),
		}));
	};

	const getOuterFilters = (atFiltersComponent?: boolean) => {
		const timeInterval = (
			customTimeInterval || <TimeIntervalRadio
				dateIntervalChange={([timeMin, timeMax], partition) => handleChange({ timeMax, timeMin, partition })}
				range={filters.timeMax && filters.timeMin ? [+filters.timeMin, +filters.timeMax] : null}
				className="pull-right mr10 ml10"
				utcOffset={utcOffset}
				utc={appointmentUtc && !filters.orderTimePriority && !filters.batchTimePriority}
				except={rangePickerFormat === 'YYYY'
					? [DashboardTimeInterval.Day, DashboardTimeInterval.Week, DashboardTimeInterval.All]
					: undefined}
			/>
		);
		return <>
			{moreFilters}
			{shouldShowFilter(AppointmentReportFilters.OuterFilters) && <>
				{shouldShowFilter(AppointmentReportFilters.TimeIntervalRadio) && (atFiltersComponent
					? <div className="filter-element">
						{timeInterval}
					</div> : timeInterval)}
				{shouldShowFilter(AppointmentReportFilters.TimeIntervalRadio)
					&& <div
						className={`pull-right input-group ${
							atFiltersComponent ? 'filters-rangepicker' : 'ml10'} ${
							priorityTimes.length > 3 ? 'input-group__long-btn' : ''}`}
						style={{ maxWidth: 252 }}
					>
						{customRangePicker || <CustomRangePicker
							value={filters.timeMax && filters.timeMin ? [+filters.timeMin, +filters.timeMax] : null}
							onChange={(date) => {
								if (date) {
									handleChange({
										timeMax: date[1],
										timeMin: date[0],
									});
								} else {
									handleChange({
										timeMax: null,
										timeMin: null,
									});
								}
							}}
							antdProps={{
								showTime: isMobile,
								classNames: { popup: 'mobile-range-picker' },
								getPopupContainer: rangePickerContainer,
							}}
							forceFormat
							placeholderRange={['Start date', 'End date']}
							format={rangePickerFormat}
							utcOffset={utcOffset ? userUtcOffset : undefined}
							utc={appointmentUtc && !filters.orderTimePriority && !filters.batchTimePriority}
						/>}
						{customTimePriorityButtons
							? customTimePriorityButtons(filters, handleChange)
							: priorityTimes?.length > 0 && (
								<div
									className={`input-group-btn ${priorityTimes?.length > 0 ? 'filter-ratio-element' : ''}`}
								>
									<Radio.Group
										// className="custom-radio"
										name="timePriority"
										value={filters.timePriority}
										onChange={(e) => handleChange({
											timePriority: e.target.value,
										})}
									>
										{priorityTimes.includes(TimePriority.Appointment)
										&& <Radio.Button
											value={TimePriority.Appointment}
											title="Appointment time"
											className="custom-radio-button"
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-calendar-check-o" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.Order)
										&& <Radio.Button
											value={TimePriority.Order}
											className="custom-radio-button"
											title="Order time"
											disabled={
												shouldShowFilter(AppointmentReportFilters.ReportSourceTypes)
													? filters.reportSourceTypes
														?.some((v) => ReportSourceTypeAppointmentTime.includes(v)
															|| ReportSourceTypeInquiry.includes(v))
													: ReportSourceTypeAppointmentTime.includes(filters.reportSourceType as any)
													|| ReportSourceTypeInquiry.includes(filters.reportSourceType as any)
											}
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-clock-o" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.Batch)
										&& <Radio.Button
											value={TimePriority.Batch}
											title="Batch time"
											className="custom-radio-button"
											disabled={
												shouldShowFilter(AppointmentReportFilters.ReportSourceTypes)
													? filters.reportSourceTypes
														?.some((v) => ReportSourceTypeAppointmentTime.includes(v)
															|| ReportSourceTypeInquiry.includes(v))
													: ReportSourceTypeAppointmentTime.includes(filters.reportSourceType as any)
													|| ReportSourceTypeInquiry.includes(filters.reportSourceType as any)
											}
										>
											<div className="custom-radio-button">
												<i className="fa fa-university" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.Inquiry)
										&& <Radio.Button
											title="Inquiry time"
											value={TimePriority.Inquiry}
											className="custom-radio-button"
											/* disabled={
												shouldShowFilter(AppointmentReportFilters.ReportSourceTypes)
													? filters.reportSourceTypes?.some(v => ReportSourceTypeOrder.includes(v))
													: ReportSourceTypeOrder.includes(+filters.reportSourceType)
											} */
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-question-circle-o" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.CreatedPatient)
										&& <Radio.Button
											title="Created Patient time"
											value={TimePriority.CreatedPatient}
											className="custom-radio-button"
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-user" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.Quote)
										&& <Radio.Button
											value={TimePriority.Quote}
											className="custom-radio-button"
											title="Main Quote Time in Order"
										>
											<div className="custom-radio-button">
												<strong>Q</strong>
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.CheckDate)
										&& <Radio.Button
											value={TimePriority.CheckDate}
											className="custom-radio-button"
											title="Claim Payment Document Check Date"
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-dollar" aria-hidden="true" />
											</div>
										</Radio.Button>}
										{priorityTimes.includes(TimePriority.ProcessedTime)
										&& <Radio.Button
											value={TimePriority.ProcessedTime}
											className="custom-radio-button"
											title="Claim Payment Processed Time"
										>
											<div className="custom-radio-button">
												<i className="tab-icon fa fa-rotate-right" aria-hidden="true" />
											</div>
										</Radio.Button>}
									</Radio.Group>
								</div>)}
						<FilterNode
							data-param="timeMax"
							filters={filters}
							filtersHandler={filtersHandler}
						>
							<div className="filter-element" />
						</FilterNode>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.RoundToggleButton) && <RoundToggleButton />}
				{shouldShowFilter(AppointmentReportFilters.Partition) && <div
					className={`pull-right ${isMobile ? 'filter-element' : ''}`}
					data-param="partition"
				>
					<PartitionFilter
						className={atFiltersComponent ? 'filter-element' : undefined}
						excludeIntervals={excludeIntervals}
						filters={filters}
						selectFiltersProps={isMobile ? { selectProps: { style: { width: '100%' } } } as any : undefined}
						handleChange={handleChange}
					/>
				</div>}
				{shouldShowFilter(AppointmentReportFilters.ReportSourceTypes) && !reportSourceTypePlaceAsSearch
					? atFiltersComponent ? <div className="filter-element">{reportSourceTypesFilter}</div> : <>{reportSourceTypesFilter}</>
					: undefined}
				{shouldShowFilter(AppointmentReportFilters.ReportSourceType) && <ReportSourceTypeSelect
					multiple={false}
					containerClassName={atFiltersComponent ? 'filter-element' : 'pull-right ml10'}
					additionalParams={filters}
					value={filters.reportSourceType}
					defaultValue={filters.reportSourceType}
					onChange={(value: Array<ReportSourceType> | ReportSourceType) => {
						const values = value as ReportSourceType;

						if (ReportSourceTypeAppointmentTime.includes(values)) {
							handleChange({
								timePriority: saveBatchTimePriority ? filters.timePriority : TimePriority.Appointment,
								reportSourceType: values,
							});
						} else if (ReportSourceTypeInquiry.includes(values)) {
							handleChange({
								timePriority: saveBatchTimePriority ? filters.timePriority : TimePriority.Inquiry,
								reportSourceType: values,
							});
						} else {
							handleChange({
								reportSourceType: values,
							});
						}
					}}
					excludeTypes={reportSourceTypeExclude}
				/>}
				{shouldShowFilter(AppointmentReportFilters.OuterInquiryStatusSelect)
					&& (!atFiltersComponent || !shouldShowFilter(AppointmentReportFilters.InnerInquiryStatusSelect))
					&& <SelectFilter
						additionalParams={filters}
						param="inquiryStatus"
						onChange={handleChange}
						selectProps={{
							style: { width: 120 },
						}}
						className="pull-right ml10"
						defaultValue={filters.inquiryStatus ?? ''}
						currentValue={filters.inquiryStatus}
					>
						<Option key="all" value="">{InquiryStatusNames[InquiryStatus.All]}</Option>
						{InquiryStatusArray.filter((item) => item !== InquiryStatus.All)
							.map((key) => <Option key={key} value={+key}>{InquiryStatusNames[key]}</Option>)}
					</SelectFilter>}
				{shouldShowFilter(AppointmentReportFilters.OuterLocationId)
					&& (!atFiltersComponent || !shouldShowFilter(AppointmentReportFilters.InnerLocationId))
					&& <div
						data-param="locationId"
						className={`pull-${locationLeft ? 'left mr' : 'right ml'}10 location-filter ${isMobile ? 'filter-element' : ''}`}
					>
						<LocationSelect
							selectProps={{ className: 'pull-right', value: filters.locationId }}
							onChange={(evt) => {
								handleChange({
									locationId: evt.currentTarget.value,
									roomIds: undefined,
								});
								setKeys((prev) => ({ ...prev, roomIds: Math.random() }));
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OuterInquiryLocationId)
					&& (!atFiltersComponent || !shouldShowFilter(AppointmentReportFilters.InnerInquiryLocationId))
					&& <div
						data-param="inquiryLocationId"
						className={`pull-right ml10 ${isMobile ? 'filter-element' : ''}`}
					>
						<LocationSelect
							selectProps={{ className: 'pull-right', value: filters.inquiryLocationId }}
							param="inquiryLocationId"
							onChange={(evt) => {
								handleChange({
									inquiryLocationId: evt.currentTarget.value,
								});
							}}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OuterStatusIds)
					&& (!atFiltersComponent || !shouldShowFilter(AppointmentReportFilters.InnerStatusIds))
					&& <div
						className="pull-right status-filter ml10"
						data-param="statusIds"
					>
						<StatusFilter
							maxTagTextLength={11}
							value={filters.statusIds ? (filters.statusIds || []).map((s) => +s) : undefined}
							onChange={(value) => handleChange({ statusIds: value })}
							getPopupContainer={getSelectFilterContainer}
						/>
					</div>}
				{shouldShowFilter(AppointmentReportFilters.OuterFilters)
					&& shouldShowFilter(AppointmentReportFilters.ReceivedValue)
					&& (
						<div
							data-param="receiveValue"
							style={{ flex: '0 0 auto', marginLeft: atFiltersComponent ? '10px' : undefined, height: 34 }}
							className="pull-right ml10"
						>
							<AccrualSwitcher
								style={{ marginTop: 6 }}
								checked={!!(filters.receivedValue ?? ReceivedValue.PaymentsBased)}
								handleChange={(params) => {
									handleChange(params);
									setKeys((prev) => ({
										...prev,
										doctorId: Math.random(),
										patientId: Math.random(),
									}));
								}}
							/>
						</div>
					)}
			</>}
			{additionalFilters && additionalFilters(filters, handleChange)}
			{atFiltersComponent && (additionalFilters || shouldShowFilter(AppointmentReportFilters.OuterFilters))
				? <div style={{ width: '100%', height: 10 }} /> : null}
		</>;
	};

	return (
		<div className={`list-filters clearfix${className ? ` ${className}` : ''}`}>
			<LeaveConfirmationModal
				when={!!selectedUserFilters?.name && filtersChanged}
				handleBlockedNavigation={(nextLocation) => {
					return nextLocation.pathname.includes(location.pathname);
				}}
				message={`Filters have been changed. Save the current filter combination into a combination named "${
					selectedUserFilters?.name}" before leaving?`}
				onOk={(leaveLocation) => {
					saveCurrentFilters(filters)
						?.then(() => setTimeout(leaveLocation, 200));
				}}
			/>
			{shouldShowFilter(AppointmentReportFilters.OuterFilters) && <>
				{shouldShowFilter(AppointmentReportFilters.Text) && <div className="list-filters__search">
					<div className="pull-left mr10" style={{ marginBottom: '15px' }}>
						<SimpleSearchInput />
					</div>
				</div>}
			</>}
			{shouldShowFilter(AppointmentReportFilters.ReportSourceTypes) && reportSourceTypePlaceAsSearch
				? <div className="list-filters__search">
					<div className="pull-left" style={{ marginBottom: '15px' }}>
						{reportSourceTypesFilter}
					</div>
				</div> : undefined}
			<div className="clearfix filters-wrapper">
				{withFiltersComponent && <FiltersComponent
					open={open}
					onOpenChange={setOpen}
					title={filterComponentTitle}
					apply
					placement={filtersPlacement}
					getPopupContainer={getFiltersContainer}
					filtersHandler={filtersHandler}
					getChildrenList={(tooltipValues) => {
						const arr: any = [];
						React.Children.forEach(tooltipValues?.props?.children?.props?.children, (el) => {
							if (el?.props?.items) {
								el?.props?.items?.map((item) => React.Children.forEach(item.children?.props?.children, (el) => arr.push(el)));
							} else {
								solveChildrenList(el?.props?.children, arr);
							}
						});
						return arr;
					}}
					popoverClassName={`${popoverClassName} appointment-filters-tooltip filters-tooltip__tabs`}
					additionalParams={{ ...filters, orderPriorityFiltering: filters.orderPriorityFiltering || undefined }}
					filtersClean={(filterNames) => {
						const params = {};
						Object.keys(filterNames || {}).forEach((key) => params[key] = Array.isArray(filters[key]) ? [] : undefined);
						forHistory && filtersForClearForHistory.forEach((key) => params[key] = undefined);
						handleChange({
							...params,
							byWorkingTimes: undefined,
							orderPriorityFiltering: false,
							priceMax: undefined,
							priceMin: undefined,
							timeMinSignedTemplate: undefined,
							...defaultFilters,
							...clearFilters,
						});
						setValues(() => ({
							...inquiryFilterData,
							...appointmentFiltersData,
							...consentFormFilterData,
							...orderFilterData,
							...patientFiltersData,
						}));
						resetKeys();
						afterInnerAdditionalFiltersClear && afterInnerAdditionalFiltersClear();
					}}
					tooltipValues={
						<div className="clearfix appointment-filters-tooltip-inner is-relative">
							<div style={{ width: '100%', margin: 0 }}>
								<div className="filters-container">
									{beforeTabsFilters && transformChildrenList(beforeTabsFilters(filters, handleChange), filters, filtersHandler)}
									{isMobile
										? React.Children.map(
											getOuterFilters(true),
											(el) => transformChildrenList(el, filters, filtersHandler, true),
										) : null}
								</div>
								<AppointmentFiltersTabs
									type="card"
									tabBarExtraContent={{
										right: <UserFiltersComponent
											selectedUserFilters={selectedUserFilters}
											setSelectedUserFilters={setSelectedUserFilters}
											actorType={initialValues?.actorType}
											messageGroup={initialValues?.messageGroup}
											filters={filters}
											onSelect={(message: any) => {
												request<any>('appointmentListFilter', message)
													.then((res) => {
														setValues(res);
														resetKeys();
													})
													.catch((err) => console.log(err));
												handleChange(message, true);
											}}
										/>,
									}}
									items={filtersTabs
										.map((item) => {
											const count = getFilters(filters, item.children?.props?.children, { filtersHandler })
												.reduce((res, filter) => (filter() ? res + 1 : res), 0);
											const onBadgeClick = () => {
												getClean(item.children?.props?.children, {
													filtersClean: (filterNames) => {
														const params = {};
														item.key === 'appointment'
														&& afterInnerAdditionalFiltersClear && afterInnerAdditionalFiltersClear();
														[...Object
															.keys(filterNames || {}), ...(additionalParams[item.key] || [])]
															.forEach((key) => {
																params[key] = clearFilters?.[key] ?? defaultFilters[key]
																?? (Array.isArray(filters[key]) ? [] : undefined);
															});
														handleChange({
															...params,
														});
														setValues((prev) => ({
															...prev,
															...(resetValues[item.key] || {}),
														}));
														resetKeys();
													},
												})();
											};

											return {
												...item,
												children: transformChildrenList(item.children, filters, filtersHandler),
												count,
												onBadgeClick,
											};
										})}
								/>
							</div>
						</div>
					}
				/>}
				{!isMobile && getOuterFilters()}
			</div>
		</div>
	);
};

export default AppointmentsFilters;
